import { FORM_DOC_TYPES } from "./formDocLayouts";
import ChimeLogo from "../assests/chime.png";
import { userHeaders } from "@/utils/userAuth";
import { getLocalTaxLabel } from "@/utils/taxRates";

// ─────────────────────────────────────────────────────────────────────────────
// Layout engine: renders admin-designed document templates.
//
// A template layout is pure data: { page: {width, height}, elements: [...] }.
// Element types:
//   text  — { type, x, y, w, content, fontSize, bold, italic, color, align, wrap }
//           content may mix static text with {tokens}, e.g. "Net Pay: ${netPay}"
//   rect  — { type, x, y, w, h, fill, stroke, lineWidth, radius }
//   line  — { type, x, y, w, h, color, lineWidth }  (drawn from (x,y) to (x+w,y+h))
//   image — { type, x, y, w, h, src }  (src is a {token} resolving to a data URL)
//   table — { type, x, y, w, binding, columns, fontSize, rowHeight, headerFill,
//             headerColor, color, zebra, rowLines }
//
// Every element supports:
//   page   — 1-based page number (default 1); pages are added as needed
//   showIf — conditional visibility: "flag", "!flag", "key=value", "key!=value"
//            evaluated against the data context (e.g. "isContractor",
//            "payType=salary", "!hasLogo"). Empty/absent = always shown.
//
// Tables that overflow the page bottom continue on the next page with their
// header repeated.
//
// The same context builders feed the editor's on-canvas preview and the real
// jsPDF output, so what the admin sees is what customers get.
// ─────────────────────────────────────────────────────────────────────────────

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// ── static brand assets ──────────────────────────────────────────────────────
// Image elements may point at a static URL (the Gusto / Chime logos the
// built-in generators draw when the customer has no logo of their own) as
// well as a {token} data URL. Static URLs are fetched once and kept as data
// URLs with their natural size so renderLayout can stay synchronous: the two
// known logos start loading as soon as this module does, and
// preloadLayoutAssets() awaits whatever a given layout needs before a render.
export const STATIC_ASSETS = { gustoLogo: "/gustoLogo.png", chimeLogo: ChimeLogo };
const assetCache = new Map();   // src → { dataUrl, width, height }
const assetLoading = new Map(); // src → Promise
function loadAsset(src) {
  if (!src) return Promise.resolve(null);
  if (assetCache.has(src)) return Promise.resolve(assetCache.get(src));
  if (assetLoading.has(src)) return assetLoading.get(src);
  const job = (async () => {
    try {
      let dataUrl = src;
      if (!src.startsWith("data:")) {
        const res = await fetch(src);
        if (!res.ok) return null;
        const blob = await res.blob();
        dataUrl = await new Promise((resolve, reject) => {
          const r = new FileReader();
          r.onerror = reject;
          r.onloadend = () => resolve(r.result);
          r.readAsDataURL(blob);
        });
      }
      const dims = await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
        img.onerror = () => resolve({ width: 0, height: 0 });
        img.src = dataUrl;
      });
      const entry = { dataUrl, ...dims };
      assetCache.set(src, entry);
      return entry;
    } catch {
      return null;
    } finally {
      assetLoading.delete(src);
    }
  })();
  assetLoading.set(src, job);
  return job;
}
if (typeof window !== "undefined") Object.values(STATIC_ASSETS).forEach((u) => { loadAsset(u); });

export function getAssetDataUrl(src) {
  const e = assetCache.get(src);
  return e ? e.dataUrl : null;
}

// Await every image a layout references (static logos and the customer's own
// data-URL logo, whose natural size is needed for `fit: "contain"`).
export async function preloadLayoutAssets(layout, templateData, documentType = "paystub") {
  let ctx = {};
  try { ctx = buildContext(templateData, documentType); } catch { /* preview still works without tokens */ }
  const srcs = ((layout && layout.elements) || [])
    .filter((el) => el && el.type === "image")
    .map((el) => resolveTokens(el.src, { ...STATIC_ASSETS, ...ctx }))
    .filter(Boolean);
  await Promise.all([...[...new Set(srcs)].map(loadAsset), ...layoutFonts(layout).flatMap((k) => [loadLayoutFont(k), loadBrowserFont(k)])]);
}

// ── formatting helpers ───────────────────────────────────────────────────────

function money(n) {
  return Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(date) {
  if (!date) return "";
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  if (typeof date === "string" && date.match(/^\d{4}-\d{2}-\d{2}/)) {
    const [y, m, d] = date.slice(0, 10).split("-").map(Number);
    return `${months[m - 1]} ${d}, ${y}`;
  }
  const d = new Date(date);
  if (isNaN(d)) return String(date);
  if (date instanceof Date) return `${months[date.getUTCMonth()]} ${date.getUTCDate()}, ${date.getUTCFullYear()}`;
  return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

function fmtMDY(date) {
  if (!date) return "";
  if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}/.test(date)) {
    const [y, m, d] = date.slice(0, 10).split("-");
    return `${m}/${d}/${y}`;
  }
  const d = new Date(date);
  if (isNaN(d)) return String(date);
  const useUtc = date instanceof Date;
  const mm = String((useUtc ? d.getUTCMonth() : d.getMonth()) + 1).padStart(2, "0");
  const dd = String(useUtc ? d.getUTCDate() : d.getDate()).padStart(2, "0");
  return `${mm}/${dd}/${useUtc ? d.getUTCFullYear() : d.getFullYear()}`;
}

function fmtLongDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d)) return String(dateStr);
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

function fmtMonthYear(s) {
  if (!s) return "";
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const m = String(s).match(/^(\d{4})-(\d{2})/);
  if (m) return `${months[Number(m[2]) - 1]} ${m[1]}`;
  const d = new Date(s);
  return isNaN(d) ? String(s) : `${months[d.getMonth()]} ${d.getFullYear()}`;
}

function maskSSN(ssn) {
  const digits = String(ssn || "").replace(/\D/g, "");
  return digits.length >= 4 ? `XXX-XX-${digits.slice(-4)}` : "";
}

function titleCase(s) {
  return String(s || "").replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
}

// Deterministic hash so generated check/employee numbers are stable per person
// (mirrors the behaviour of the built-in OnPay template).
function stableHash(str) {
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h + str.charCodeAt(i)) & 0xffffffff;
  return Math.abs(h);
}

function hexToRgb(hex) {
  const h = String(hex || "#000000").replace("#", "");
  const v = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  return [parseInt(v.slice(0, 2), 16) || 0, parseInt(v.slice(2, 4), 16) || 0, parseInt(v.slice(4, 6), 16) || 0];
}

// ── data contexts ────────────────────────────────────────────────────────────

function buildPaystubContext(td) {
  const f = td.formData || {};
  const ctx = {
    company: f.company || "",
    companyAddress: f.companyAddress || "",
    companyCityStateZip: [f.companyCity, f.companyState, f.companyZip].filter(Boolean).join(", "),
    companyPhone: f.companyPhone || "",
    employeeName: f.name || "",
    employeeAddress: f.address || "",
    employeeCityStateZip: [f.city, f.state, f.zip].filter(Boolean).join(", "),
    ssn: maskSSN(f.ssn),
    employeeId: f.employeeId || f.fileNum || "",
    filingStatus: f.federalFilingStatus || "",
    startDate: fmtDate(td.startDate),
    endDate: fmtDate(td.endDate),
    payDate: fmtDate(td.payDate),
    payPeriod: `${fmtDate(td.startDate)} - ${fmtDate(td.endDate)}`,
    payFrequency: td.payFrequency || "",
    stubLabel: td.totalStubs > 1 ? `${td.stubNum} of ${td.totalStubs}` : "",
    rate: money(td.rate),
    hours: String(td.hours ?? ""),
    overtime: String(td.overtime ?? ""),
    regularPay: money(td.regularPay),
    overtimePay: money(td.overtimePay),
    grossPay: money(td.grossPay),
    netPay: money(td.netPay),
    federalTax: money(td.federalTax),
    stateTax: money(td.stateTax),
    localTax: money(td.localTax),
    ssTax: money(td.ssTax),
    medTax: money(td.medTax),
    totalTax: money(td.totalTax),
    totalDeductions: money((td.totalTax || 0) + (td.totalDeductions || 0) + (td.totalContributions || 0)),
    ytdGrossPay: money(td.ytdGrossPay),
    ytdNetPay: money(td.ytdNetPay),
    ytdTotalTax: money(td.ytdTotalTax),
    ytdFederalTax: money(td.ytdFederalTax),
    ytdStateTax: money(td.ytdStateTax),
    ytdSsTax: money(td.ytdSsTax),
    ytdMedTax: money(td.ytdMedTax),
    ytdHours: String(td.ytdHours ?? ""),
    logoDataUrl: td.logoDataUrl || "",
    // condition flags
    payType: td.payType || "hourly",
    workerType: td.workerType || "employee",
    isContractor: td.isContractor ? "true" : "",
    hasLogo: td.logoDataUrl ? "true" : "",
    hasOvertime: Number(td.overtime) > 0 ? "true" : "",
    hasCommission: Number(td.commission) > 0 ? "true" : "",
    workerLabel: td.isContractor ? "CONTRACTOR" : "EMPLOYEE",
    workerLabelTitle: td.isContractor ? "Contractor" : "Employee",
    statementTitle: td.isContractor ? "Contractor Payment Statement" : "Earnings Statement",
    earningsTitle: td.isContractor ? "Contractor Gross Earnings" : "Employee Gross Earnings",
    bankLast4: String(f.bank || "").slice(-4) || "0000",
    bankLast4Raw: f.bank || "0000",
    ssnLine: td.isContractor ? `EIN/SSN: ***-**-${String(f.ssn || "0000").replace(/\D/g, "").slice(-4) || "0000"}` : `XXX-XX-${String(f.ssn || "0000").replace(/\D/g, "").slice(-4) || "0000"}`,
    companyCityLine: `${f.companyCity || ""}, ${f.companyState || ""} ${f.companyZip || ""}`,
    employeeCityLine: `${f.city || ""}, ${f.state || ""} ${f.zip || ""}`,
    companyCityState: `${f.companyCity || "City"}, ${f.companyState || "ST"}`,
    companyZip: f.companyZip || "00000",
    payDateMDY: fmtMDY(td.payDate), startDateMDY: fmtMDY(td.startDate), endDateMDY: fmtMDY(td.endDate),
    netPayDollar: `$${money(td.netPay)}`,
    checkNumber: td.periodCheckNumber || String(1 + (stableHash((f.name || "emp") + (td.stubNum || 1)) % 999)),
    employeeNumber: f.employeeId || String(1000000 + (stableHash(f.name || "emp") % 9000000)),
    memo: td.periodMemo || "Thank you for your hard work.",
  };

  const ytdPeriods = td.ytdPayPeriods || 1;
  const isSalary = td.payType === "salary";

  const earnings = [];
  if (isSalary) {
    earnings.push({
      name: "Salary", nameDetailed: "Salary | Per Period",
      rate: "-", rateDetailed: `$${money(td.annualSalary)}/yr`,
      hours: "-", current: money(td.regularPay), ytd: money(td.ytdRegularPay),
    });
  } else {
    earnings.push({
      name: "Regular", nameDetailed: "Regular Hours | Hourly",
      rate: money(td.rate), rateDetailed: `$${money(td.rate)}`,
      hours: String(td.hours ?? ""), current: money(td.regularPay), ytd: money(td.ytdRegularPay),
    });
  }
  if (Number(td.overtime) > 0) {
    earnings.push({
      name: "Overtime", nameDetailed: "Overtime Hours | 1.5x",
      rate: money(td.rate * 1.5), rateDetailed: `$${money(td.rate * 1.5)}`,
      hours: String(td.overtime), current: money(td.overtimePay), ytd: money(td.ytdOvertimePay),
    });
  }
  if (Number(td.commission) > 0) {
    earnings.push({ name: "Commission", nameDetailed: "Commission", rate: "-", rateDetailed: "-", hours: "-", current: money(td.commission), ytd: money(td.ytdCommission) });
  }
  if (Number(td.tips) > 0) {
    const tl = td.tipsCash ? "Cash Tips" : "Tips";
    earnings.push({ name: tl, nameDetailed: tl, rate: "-", rateDetailed: "-", hours: "-", current: money(td.tips), ytd: money(td.ytdTips) });
  }
  ctx.earnings = earnings;

  // ── Gusto-style row groups (employee vs employer taxes, itemized
  //    deductions/contributions with tax-type labels, and a summary table) ──
  const fedStatus = f.federalFilingStatus === "married_jointly" ? " (MFJ)" : f.federalFilingStatus === "head_of_household" ? " (HOH)" : f.federalFilingStatus ? " (S)" : "";
  const employeeTaxes = [
    { name: `Federal Income Tax${fedStatus}`, current: money(td.federalTax), ytd: money(td.ytdFederalTax) },
    { name: "Social Security (6.2%)", current: money(td.ssTax), ytd: money(td.ytdSsTax) },
    { name: "Medicare (1.45%)", current: money(td.medTax), ytd: money(td.ytdMedTax) },
  ];
  {
    // The built-in Gusto template always lists the state row
    const allow = parseInt(f.stateAllowances) > 0 ? ` (${f.stateAllowances} allow.)` : "";
    employeeTaxes.push({ name: `${(f.state || "State").toUpperCase()} Tax${allow}`, current: money(td.stateTax), ytd: money(td.ytdStateTax) });
  }
  if (f.includeLocalTax && Number(td.localTax) > 0) {
    const rate = Number(td.localTaxRate) > 0 ? ` (${(td.localTaxRate * 100).toFixed(2)}%)` : "";
    employeeTaxes.push({ name: `${getLocalTaxLabel(f)} Tax${rate}`, current: money(td.localTax), ytd: money(td.ytdLocalTax) });
  }
  ctx.employeeTaxes = employeeTaxes;

  const gp = Number(td.grossPay) || 0;
  const ygp = Number(td.ytdGrossPay) || 0;
  const suta = td.sutaRate || 0.027;
  const futa = td.futaRate || 0.006; // the form's Tax Settings
  ctx.employerTaxes = [
    { name: "Social Security (6.2%)", current: money(gp * 0.062), ytd: money(ygp * 0.062) },
    { name: "Medicare (1.45%)", current: money(gp * 0.0145), ytd: money(ygp * 0.0145) },
    { name: `FUTA (${+(futa * 100).toFixed(2)}%)`, current: money(gp * futa), ytd: money(ygp * futa) },
    { name: `${(f.state || "State").toUpperCase()} Unemp. (${(suta * 100).toFixed(2)}%)`, current: money(gp * suta), ytd: money(ygp * suta) },
  ];

  const itemize = (items, fallbackLabel) => {
    const rows = (items || []).map((d) => ({
      name: d.name || fallbackLabel,
      taxType: d.preTax ? "Pre-Tax" : "Post-Tax",
      current: money(d.currentAmount),
      ytd: money((d.currentAmount || 0) * ytdPeriods),
    }));
    return rows.length ? rows : [{ name: "None", taxType: "–", current: "0.00", ytd: "0.00" }];
  };
  ctx.deductionItems = itemize(td.deductionsData, "Deduction");
  ctx.contributionItems = itemize(td.contributionsData, "Contribution");

  // Summary rows carry display strings exactly as the built-in Gusto template
  // prints them: money with a $ sign, hours as a plain 2-decimal number.
  const usd = (n) => `$${money(n)}`;
  const summary = [{ name: "Gross Earnings", current: usd(td.grossPay), ytd: usd(td.ytdGrossPay) }];
  if (td.isContractor) {
    summary.push({ name: "Taxes Withheld", current: "$0.00", ytd: "$0.00" });
    summary.push({ name: "Total Payment", current: usd(td.grossPay), ytd: usd(td.ytdGrossPay) });
  } else {
    summary.push({ name: "Pre-Tax Deductions/Contributions", current: usd(td.totalPreTax), ytd: usd(td.ytdPreTax) });
    summary.push({ name: "Taxes", current: usd(td.totalTax), ytd: usd(td.ytdTotalTax) });
    summary.push({ name: "Post-Tax Deductions/Contributions", current: usd(td.totalPostTax), ytd: usd(td.ytdPostTax) });
    summary.push({ name: "Net Pay", current: usd(td.netPay), ytd: usd(td.ytdNetPay) });
    summary.push({ name: "Total Reimbursements", current: "$0.00", ytd: "$0.00" });
    summary.push({ name: "Check Amount", current: usd(td.netPay), ytd: usd(td.ytdNetPay) });
  }
  if (!isSalary) {
    summary.push({ name: "Hours Worked", current: money((Number(td.hours) || 0) + (Number(td.overtime) || 0)), ytd: money(td.ytdHours) });
  }
  ctx.summary = summary;

  // ── OnPay check-stub rows (Courier grid: fixed decimals, short tax codes) ──
  const fx3 = (n) => (Number(n) > 0 ? Number(n).toFixed(3) : "0.000");
  const onpayEarnings = [
    { desc: "Regular", hours: fx3(td.hours), rate: Number(td.rate) > 0 ? money(td.rate) : "0.00", amt: money(td.regularPay), ytd: money(td.ytdRegularPay) },
    { desc: "Overtime", hours: fx3(td.overtime), rate: Number(td.rate) > 0 ? money(td.rate * 1.5) : "0.00", amt: money(td.overtimePay), ytd: money(td.ytdOvertimePay) },
  ];
  if (Number(td.commission) > 0) onpayEarnings.push({ desc: "Commission", hours: "0.000", rate: "0.00", amt: money(td.commission), ytd: money(td.ytdCommission) });
  if (Number(td.tips) > 0) onpayEarnings.push({ desc: td.tipsCash ? "Cash Tips" : "Tips", hours: "0.000", rate: "0.00", amt: money(td.tips), ytd: money(td.ytdTips) });
  ctx.onpayEarnings = onpayEarnings;
  ctx.onpayTaxes = [
    { desc: "SS", amt: money(td.ssTax), ytd: money(td.ytdSsTax) },
    { desc: "FIT", amt: money(td.federalTax), ytd: money(td.ytdFederalTax) },
    { desc: "MEDI", amt: money(td.medTax), ytd: money(td.ytdMedTax) },
    { desc: `SIT-${f.state || "ST"}`, amt: money(td.stateTax), ytd: money(td.ytdStateTax) },
  ];
  ctx.onpayDeductions = [
    ...(td.deductionsData || []).map((d) => ({ desc: String(d.name || d.type || "Deduction").substring(0, 10), ben: "", amt: money(d.currentAmount), ytd: money((d.currentAmount || 0) * ytdPeriods) })),
    ...(td.contributionsData || []).map((c) => ({ desc: String(c.name || c.type || "Contrib").substring(0, 10), ben: "", amt: money(c.currentAmount), ytd: money((c.currentAmount || 0) * ytdPeriods) })),
  ];
  ctx.onpayTotalDed = money((Number(td.totalDeductions) || 0) + (Number(td.totalContributions) || 0));
  ctx.onpayYtdDed = money((Number(td.ytdDeductions) || 0) + (Number(td.ytdContributions) || 0));
  ctx.onpayYtdTax = money((Number(td.ytdSsTax) || 0) + (Number(td.ytdMedTax) || 0) + (Number(td.ytdFederalTax) || 0) + (Number(td.ytdStateTax) || 0));

  const isCanadian = td.cpp !== undefined || td.ei !== undefined;
  const isUk = td.payeTax !== undefined || td.nationalInsurance !== undefined;
  const deductions = [];
  if (isUk) {
    deductions.push({ name: "PAYE Income Tax", current: money(td.payeTax), ytd: money(td.ytdPayeTax) });
    deductions.push({ name: "National Insurance", current: money(td.nationalInsurance), ytd: money(td.ytdNationalInsurance) });
    if (Number(td.studentLoan) > 0) deductions.push({ name: "Student Loan", current: money(td.studentLoan), ytd: money(td.ytdStudentLoan) });
    ctx.payeTax = money(td.payeTax); ctx.nationalInsurance = money(td.nationalInsurance); ctx.studentLoan = money(td.studentLoan);
    ctx.ytdPayeTax = money(td.ytdPayeTax); ctx.ytdNationalInsurance = money(td.ytdNationalInsurance); ctx.ytdStudentLoan = money(td.ytdStudentLoan);
    ctx.taxCode = td.taxCode || f.taxCode || ""; ctx.niNumber = td.niNumber || f.niNumber || "";
    // the OnPay check-stub tax rows read PAYE / NI / SL on a UK payslip
    ctx.onpayTaxes = [
      { desc: "PAYE", amt: money(td.payeTax), ytd: money(td.ytdPayeTax) },
      { desc: "NI", amt: money(td.nationalInsurance), ytd: money(td.ytdNationalInsurance) },
      ...(Number(td.studentLoan) > 0 ? [{ desc: "SL", amt: money(td.studentLoan), ytd: money(td.ytdStudentLoan) }] : []),
    ];
    ctx.onpayYtdTax = money((Number(td.ytdPayeTax) || 0) + (Number(td.ytdNationalInsurance) || 0) + (Number(td.ytdStudentLoan) || 0));
    ctx.onpayEarnings = ctx.onpayEarnings.map((r) => (r.desc === "Regular" ? { ...r, desc: "Basic Pay" } : r));
  } else if (isCanadian) {
    deductions.push({ name: "Federal Tax", current: money(td.federalTax), ytd: money(td.ytdFederalTax) });
    if (Number(td.provincialTax) > 0) deductions.push({ name: "Provincial Tax", current: money(td.provincialTax), ytd: money(td.ytdProvincialTax) });
    deductions.push({ name: td.cppLabel || "CPP", current: money(td.cpp), ytd: money(td.ytdCpp) });
    deductions.push({ name: "EI", current: money(td.ei), ytd: money(td.ytdEi) });
    if (Number(td.qpip) > 0) deductions.push({ name: "QPIP", current: money(td.qpip), ytd: money(td.ytdQpip) });
    ctx.cpp = money(td.cpp); ctx.ei = money(td.ei); ctx.qpip = money(td.qpip);
    ctx.provincialTax = money(td.provincialTax);
    ctx.cppLabel = td.cppLabel || "CPP";
    ctx.ytdCpp = money(td.ytdCpp); ctx.ytdEi = money(td.ytdEi);
    ctx.ytdProvincialTax = money(td.ytdProvincialTax);
  } else {
    deductions.push({ name: "Federal Income Tax", current: money(td.federalTax), ytd: money(td.ytdFederalTax) });
    deductions.push({ name: "Social Security", current: money(td.ssTax), ytd: money(td.ytdSsTax) });
    deductions.push({ name: "Medicare", current: money(td.medTax), ytd: money(td.ytdMedTax) });
    if (Number(td.stateTax) > 0) deductions.push({ name: "State Income Tax", current: money(td.stateTax), ytd: money(td.ytdStateTax) });
    if (Number(td.localTax) > 0) deductions.push({ name: `${getLocalTaxLabel(f)} Tax`, current: money(td.localTax), ytd: money(td.ytdLocalTax) });
  }
  (td.deductionsData || []).forEach((d) => {
    deductions.push({ name: d.name || "Deduction", current: money(d.currentAmount), ytd: money((d.currentAmount || 0) * ytdPeriods) });
  });
  (td.contributionsData || []).forEach((c) => {
    deductions.push({ name: c.name || "Contribution", current: money(c.currentAmount), ytd: money((c.currentAmount || 0) * ytdPeriods) });
  });
  ctx.deductions = deductions;

  return ctx;
}

function buildOfferLetterContext(td) {
  const f = td.formData || {};
  const compUnit = f.compensationType === "hourly" ? "per hour" : f.compensationType === "monthly" ? "per month" : "per year";
  const compAmount = f.compensationAmount
    ? `$${Number(f.compensationAmount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : "";
  const offerDetails = [
    { label: "Position", value: f.jobTitle },
    { label: "Department", value: f.department },
    { label: "Employment Type", value: titleCase(f.employmentType) },
    { label: "Work Location", value: titleCase(f.workLocation) },
    { label: "Start Date", value: fmtLongDate(f.startDate) },
    { label: "Reporting To", value: f.reportingManager ? `${f.reportingManager}${f.reportingTitle ? `, ${f.reportingTitle}` : ""}` : "" },
  ].filter((r) => r.value);

  return {
    companyName: f.companyName || "",
    companyAddress: f.companyAddress || "",
    companyCityStateZip: [f.companyCity, f.companyState, f.companyZip].filter(Boolean).join(", "),
    companyPhone: f.companyPhone || "",
    companyEmail: f.companyEmail || "",
    letterDate: fmtLongDate(f.letterDate) || fmtLongDate(new Date().toISOString()),
    candidateName: f.candidateName || "",
    candidateAddress: f.candidateAddress || "",
    candidateCityStateZip: [f.candidateCity, f.candidateState, f.candidateZip].filter(Boolean).join(", "),
    jobTitle: f.jobTitle || "",
    department: f.department || "",
    employmentType: titleCase(f.employmentType),
    workLocation: titleCase(f.workLocation),
    startDate: fmtLongDate(f.startDate),
    compensation: compAmount ? `${compAmount} ${compUnit}` : "",
    payFrequency: titleCase(f.payFrequency),
    benefits: f.benefits || "",
    additionalTerms: f.additionalTerms || "",
    responseDeadline: fmtLongDate(f.responseDeadline),
    signerName: f.signerName || "",
    signerTitle: f.signerTitle || "",
    logoDataUrl: f.companyLogo || "",
    hasLogo: f.companyLogo ? "true" : "",
    hasBenefits: f.benefits ? "true" : "",
    hasDeadline: f.responseDeadline ? "true" : "",
    offerDetails,
  };
}

function buildLegalDocumentContext(td) {
  const f = td.formData || {};
  const csz = (c, s, z) => [c, s, z].filter(Boolean).join(", ");
  return {
    documentTitle: f.documentTitle || "Agreement",
    effectiveDate: fmtLongDate(f.effectiveDate) || fmtLongDate(new Date().toISOString()),
    governingState: f.governingState || "",
    todayDate: fmtLongDate(new Date().toISOString()),
    partyAName: f.partyAName || "",
    partyATitle: f.partyATitle || "",
    partyAAddress: f.partyAAddress || "",
    partyACityStateZip: csz(f.partyACity, f.partyAState, f.partyAZip),
    partyAEmail: f.partyAEmail || "",
    partyAPhone: f.partyAPhone || "",
    partyBName: f.partyBName || "",
    partyBTitle: f.partyBTitle || "",
    partyBAddress: f.partyBAddress || "",
    partyBCityStateZip: csz(f.partyBCity, f.partyBState, f.partyBZip),
    partyBEmail: f.partyBEmail || "",
    partyBPhone: f.partyBPhone || "",
    recitals: f.recitals || "",
    terms: f.terms || "",
    additionalTerms: f.additionalTerms || "",
    // signatures — image (drawn/uploaded) or typed name in script style
    partyASignature: f.partyASignatureImage || "",
    partyASignatureName: f.partyASignatureName || f.partyAName || "",
    partyASignDate: fmtLongDate(f.partyASignDate) || "",
    partyBSignature: f.partyBSignatureImage || "",
    partyBSignatureName: f.partyBSignatureName || f.partyBName || "",
    partyBSignDate: fmtLongDate(f.partyBSignDate) || "",
    notaryState: f.notaryState || f.governingState || "",
    notaryCounty: f.notaryCounty || "",
    // condition flags
    hasPartyASignatureImage: f.partyASignatureImage ? "true" : "",
    hasPartyBSignatureImage: f.partyBSignatureImage ? "true" : "",
    hasRecitals: f.recitals ? "true" : "",
    hasAdditionalTerms: f.additionalTerms ? "true" : "",
    hasGoverningState: f.governingState ? "true" : "",
    parties: [
      { role: "Party A", name: f.partyAName || "", address: f.partyAAddress || "", cityStateZip: csz(f.partyACity, f.partyAState, f.partyAZip) },
      { role: "Party B", name: f.partyBName || "", address: f.partyBAddress || "", cityStateZip: csz(f.partyBCity, f.partyBState, f.partyBZip) },
    ],
  };
}

function buildResumeContext(td) {
  const r = td.formData || {};
  const p = r.personalInfo || {};
  const skills = [
    ...((r.optimizedSkills && r.optimizedSkills.technical) || []),
    ...((r.optimizedSkills && r.optimizedSkills.soft) || []),
    ...((r.optimizedSkills && r.optimizedSkills.other) || []),
  ].filter(Boolean);
  const experience = (r.optimizedExperience || []).map((e) => ({
    position: e.position || "",
    company: e.company || "",
    location: e.location || "",
    companyLine: [e.company, e.location].filter(Boolean).join(" | "),
    dates: `${fmtMonthYear(e.startDate)} - ${e.current ? "Present" : fmtMonthYear(e.endDate)}`,
    bulletsText: (e.bullets || []).filter(Boolean).map((b) => `• ${b}`).join("\n"),
  }));
  const education = (r.education || []).map((e) => ({
    degree: [e.degree, e.field].filter(Boolean).join(" in "),
    institution: e.institution || "",
    gpa: e.gpa || "",
    institutionLine: e.gpa ? `${e.institution} | GPA: ${e.gpa}` : (e.institution || ""),
    date: fmtMonthYear(e.graduationDate),
  }));
  return {
    fullName: p.fullName || "",
    email: p.email || "", phone: p.phone || "", location: p.location || "",
    linkedin: p.linkedin || "", website: p.website || "",
    contactLine: [p.email, p.phone, p.location, p.linkedin, p.website].filter(Boolean).join("  •  "),
    summary: r.professionalSummary || "",
    skillsLine: skills.join("  •  "),
    experienceText: experience
      .map((e) => [`${e.position}${e.dates.trim() !== "-" ? `  (${e.dates})` : ""}`, e.companyLine, e.bulletsText].filter(Boolean).join("\n"))
      .join("\n\n"),
    educationText: education
      .map((e) => [`${e.degree}${e.date ? `  (${e.date})` : ""}`, e.institutionLine].filter(Boolean).join("\n"))
      .join("\n\n"),
    hasSummary: r.professionalSummary ? "true" : "",
    hasSkills: skills.length ? "true" : "",
    hasLinkedin: p.linkedin ? "true" : "",
    hasWebsite: p.website ? "true" : "",
    experience,
    education,
    skillRows: skills.map((s) => ({ name: s })),
  };
}

function buildBankStatementContext(td) {
  const f = td.formData || {};
  const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const parseAmt = (v) => { const n = parseFloat(String(v ?? "").replace(/[^0-9.-]/g, "")); return isNaN(n) ? 0 : n; };
  const shortDate = (s) => {
    const [y, m, d] = String(s || "").split("-").map(Number);
    if (!y || !m || !d) return String(s || "");
    return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };
  const slashDate = (s) => {          // 8/01/2026, as the Chime statement prints it
    const [y, m, d] = String(s || "").split("-").map(Number);
    if (!y || !m || !d) return String(s || "");
    return `${m}/${String(d).padStart(2, "0")}/${y}`;
  };
  const longDate = (dt) => (dt ? dt.toLocaleString("en-US", { month: "long", day: "2-digit", year: "numeric" }) : "");
  const addDays = (s, n) => {
    const [y, m, d] = String(s || "").split("-").map(Number);
    if (!y || !m || !d) return s;
    const dt = new Date(y, m - 1, d + n);
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
  };

  const [year, month] = String(f.selectedMonth || "").split("-").map(Number);
  const start = year && month ? new Date(year, month - 1, 1) : null;
  const end = year && month ? new Date(year, month, 0) : null;
  const monthText = start ? start.toLocaleDateString("en-US", { month: "long", year: "numeric" }) : "";

  const beginning = parseAmt(f.beginningBalance);
  let running = beginning;
  let deposits = 0, purchases = 0, transfers = 0, refunds = 0, withdrawals = 0;
  const rawTx = Array.isArray(f.transactions) ? f.transactions : [];
  const transactions = rawTx
    .filter((t) => t && (t.description || t.amount))
    .map((t) => {
      const amt = parseAmt(t.amount);
      const credit = t.type === "Deposit" || t.type === "Refund";
      if (t.type === "Deposit") deposits += amt;
      else if (t.type === "Refund") refunds += amt;
      else if (t.type === "Transfer") transfers += amt;
      else if (t.type === "Withdrawal") withdrawals += amt;
      else purchases += amt;
      running += credit ? amt : -amt;
      const typeLc = String(t.type || "").toLowerCase();
      const fullDesc = String(t.description || "").trim();
      // Chime prints a cleaned, title-cased store name with the raw descriptor
      // under it; transfers stay on one line.
      let store = fullDesc.replace(/\s*#\d+/g, "").replace(/\s*\d{4,}/g, "").replace(/\s+[A-Z]{2}$/g, "").replace(/\s+/g, " ").trim();
      store = store.toLowerCase().split(" ").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
      const isTransfer = typeLc.includes("transfer");
      const settleSameDay = ["deposit", "transfer", "refund"].some((k) => typeLc.includes(k));
      return {
        date: shortDate(t.date),
        dateShort: slashDate(t.date),
        rawDate: t.date || "",
        description: t.description || "",
        descriptionStore: isTransfer ? fullDesc : store,
        descriptionSub: isTransfer ? "" : fullDesc,
        type: t.type || "",
        amount: money(amt),
        signedAmount: `${credit ? "+" : "-"}${money(amt)}`,
        amountSigned: `${credit ? "" : "-"}${money(amt)}`,
        settlementDate: settleSameDay ? slashDate(t.date) : slashDate(addDays(t.date, 1)),
        balance: money(running),
      };
    });

  const summaryRows = [
    { label: "Beginning Balance", value: money(beginning) },
    { label: "Deposits", value: money(deposits) },
    { label: "Purchases", value: money(purchases) },
    { label: "Withdrawals", value: money(withdrawals) },
    { label: "Transfers", value: money(transfers) },
    { label: "Refunds", value: money(refunds) },
    { label: "Ending Balance", value: money(running) },
  ];

  // The Chime statement's summary block, in its order and wording
  const chimeSummaryRows = [
    { label: start ? `Beginning balance on ${longDate(start)}` : "Beginning balance", value: money(beginning) },
    { label: "Deposits", value: money(deposits) },
    { label: "ATM Withdrawals", value: money(withdrawals) },
    { label: "Purchases", value: money(purchases) },
    { label: "Adjustments", value: money(0) },
    { label: "Transfers", value: money(transfers) },
    { label: "Round Up Transfers", value: money(0) },
    { label: "Fees", value: money(0) },
    { label: "SpotMe Tips", value: money(0) },
    { label: end ? `Ending balance on ${longDate(end)}` : "Ending balance", value: money(running), color: "#1a51aa" },
  ];

  const acct = String(f.accountNumber || "");
  return {
    statementStartLong: longDate(start),
    statementEndLong: longDate(end),
    statementRangeLong: start && end ? `(${longDate(start)} - ${longDate(end)})` : "",
    chimeSummaryRows,
    bankName: f.bankName || "Chime",
    accountName: f.accountName || "",
    accountAddress1: f.accountAddress1 || "",
    accountAddress2: f.accountAddress2 || "",
    accountNumber: acct,
    accountNumberMasked: acct ? `••••${acct.slice(-4)}` : "",
    monthText,
    dateRange: start && end ? `${shortDate(f.selectedMonth + "-01")} - ${end.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}` : "",
    statementStart: start ? shortDate(`${f.selectedMonth}-01`) : "",
    statementEnd: end ? end.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "",
    beginningBalance: money(beginning),
    endingBalance: money(running),
    totalDeposits: money(deposits),
    totalPurchases: money(purchases),
    totalWithdrawals: money(withdrawals),
    totalTransfers: money(transfers),
    totalRefunds: money(refunds),
    transactionCount: String(transactions.length),
    logoDataUrl: f.bankLogo || "",
    hasLogo: f.bankLogo ? "true" : "",
    hasTransactions: transactions.length ? "true" : "",
    transactions,
    summaryRows,
  };
}

const CONTEXT_BUILDERS = {
  paystub: buildPaystubContext,
  "canadian-paystub": buildPaystubContext,
  "uk-payslip": buildPaystubContext,
  "offer-letter": buildOfferLetterContext,
  "legal-document": buildLegalDocumentContext,
  resume: buildResumeContext,
  "bank-statement": buildBankStatementContext,
};
for (const [type, def] of Object.entries(FORM_DOC_TYPES)) CONTEXT_BUILDERS[type] = def.buildContext;
const FORM_DOC_KEYS = Object.keys(FORM_DOC_TYPES);

export function buildContext(templateData, documentType = "paystub", raw = false) {
  // Blank canvas: merge every category's sample context so any field from
  // any category resolves in the editor preview. Paystub is merged last so
  // its table bindings win the few name collisions (e.g. "summary").
  if (documentType === "blank") {
    const merged = {};
    for (const t of [...FORM_DOC_KEYS, "resume", "legal-document", "offer-letter", "bank-statement", "canadian-paystub", "uk-payslip", "paystub"]) {
      try { Object.assign(merged, buildContext(getSampleVariants(t)[0].data, t)); } catch { /* keep going */ }
    }
    return merged;
  }
  // Admin-created custom category (cat-*): tokens are free-form and filled
  // directly by the customer, so the context is the raw {token: value} map —
  // layered over the merged blank sample so any built-in tokens the admin
  // used still resolve in editor previews.
  if (raw || (typeof documentType === "string" && (documentType.startsWith("cat-") || documentType.startsWith("ucat-")))) {
    const merged = buildContext({}, "blank");
    // A custom template fills its own tables, so drop the sample rows the blank
    // merge brings — an empty binding renders no rows until the customer adds
    // line items (and their rows, an array, must pass through below).
    for (const k of Object.keys(merged)) if (Array.isArray(merged[k])) delete merged[k];
    merged.todayDate = new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
    for (const [k, v] of Object.entries(templateData || {})) {
      if (v === undefined || v === null) continue;
      if (Array.isArray(v) || typeof v !== "object") merged[k] = v;
    }
    return merged;
  }
  return (CONTEXT_BUILDERS[documentType] || buildPaystubContext)(templateData);
}

// Every {token} a layout's text elements reference — used to auto-build the
// customer-facing form for admin-created custom-category templates. Table
// cell tokens are row-scoped (they come from bindings), so tables are
// skipped; engine-managed tokens are excluded.
const ENGINE_TOKENS = new Set(["logoDataUrl", "todayDate"]);
export function extractLayoutTokens(layout) {
  const tokens = [];
  const seen = new Set();
  for (const el of (layout && layout.elements) || []) {
    if (!el || el.type === "table") continue;
    const text = String(el.content ?? "");
    for (const m of text.matchAll(/\{([\w.]+)\}/g)) {
      const key = m[1];
      if (!seen.has(key) && !ENGINE_TOKENS.has(key)) { seen.add(key); tokens.push(key); }
    }
  }
  return tokens;
}

// The bound tables a custom template exposes for line-item entry: each is
// { binding, columns: [{ header, key }] } where key is the row token name.
export function extractLayoutTables(layout) {
  const out = [];
  const seen = new Set();
  for (const el of (layout && layout.elements) || []) {
    if (!el || el.type !== "table" || !el.binding || seen.has(el.binding)) continue;
    seen.add(el.binding);
    const columns = (el.columns || []).map((c) => {
      const m = /\{([\w.]+)\}/.exec(String(c.token || ""));
      return { header: String(c.header || "").trim(), key: m ? m[1] : null };
    }).filter((c) => c.key);
    if (columns.length) out.push({ binding: el.binding, columns });
  }
  return out;
}

export function resolveTokens(str, ctx) {
  return String(str ?? "").replace(/\{([\w.]+)\}/g, (m, key) => {
    const v = ctx[key];
    return v === undefined || v === null ? "" : String(v);
  });
}

// ── conditional visibility ───────────────────────────────────────────────────
// Syntax: "flag" (truthy), "!flag", "key=value", "key!=value".
export function evalShowIf(showIf, ctx) {
  const expr = String(showIf || "").trim();
  if (!expr) return true;
  const neq = expr.match(/^([\w.]+)\s*!=\s*(.+)$/);
  if (neq) return String(ctx[neq[1]] ?? "") !== neq[2].trim();
  const eq = expr.match(/^([\w.]+)\s*=\s*(.+)$/);
  if (eq) return String(ctx[eq[1]] ?? "") === eq[2].trim();
  if (expr.startsWith("!")) return !ctx[expr.slice(1)];
  return !!ctx[expr];
}

// ── renderer ─────────────────────────────────────────────────────────────────

function setTextStyle(doc, el) {
  const style = el.bold && el.italic ? "bolditalic" : el.bold ? "bold" : el.italic ? "italic" : "normal";
  const custom = LAYOUT_FONTS[el.font];
  if (custom && doc.__layoutFonts && doc.__layoutFonts.has(el.font)) doc.setFont(custom.family, "normal");
  else if (custom) doc.setFont("times", "italic");
  else doc.setFont(el.font || "helvetica", style);
  doc.setFontSize(el.fontSize || 9);
  const [r, g, b] = hexToRgb(el.color || "#1a1a1a");
  doc.setTextColor(r, g, b);
}

function drawText(doc, el, ctx) {
  setTextStyle(doc, el);
  const content = resolveTokens(el.content, ctx);
  if (!content) return;
  const size = el.fontSize || 9;
  const baseY = el.y + size * 0.85;
  const opts = {};
  if (el.wrap && el.w) opts.maxWidth = el.w;
  if (el.lineHeight) opts.lineHeightFactor = el.lineHeight;
  if (el.align === "center") {
    doc.text(content, el.x + (el.w || 0) / 2, baseY, { ...opts, align: "center" });
  } else if (el.align === "right") {
    doc.text(content, el.x + (el.w || 0), baseY, { ...opts, align: "right" });
  } else {
    doc.text(content, el.x, baseY, opts);
  }
}

function drawRect(doc, el) {
  const hasFill = el.fill && el.fill !== "none";
  const hasStroke = el.stroke && el.stroke !== "none";
  if (!hasFill && !hasStroke) return;
  if (hasFill) { const [r, g, b] = hexToRgb(el.fill); doc.setFillColor(r, g, b); }
  if (hasStroke) { const [r, g, b] = hexToRgb(el.stroke); doc.setDrawColor(r, g, b); doc.setLineWidth(el.lineWidth || 0.5); }
  const mode = hasFill && hasStroke ? "FD" : hasFill ? "F" : "S";
  const dashed = Array.isArray(el.dash) && el.dash.length > 0;
  if (dashed) { try { doc.setLineDashPattern(el.dash, 0); } catch { /* solid */ } }
  if (el.radius) doc.roundedRect(el.x, el.y, el.w, el.h, el.radius, el.radius, mode);
  else doc.rect(el.x, el.y, el.w, el.h, mode);
  if (dashed) { try { doc.setLineDashPattern([], 0); } catch { /* solid */ } }
}

function drawLine(doc, el) {
  const [r, g, b] = hexToRgb(el.color || "#cbd5e1");
  doc.setDrawColor(r, g, b);
  doc.setLineWidth(el.lineWidth || 0.5);
  const dashed = Array.isArray(el.dash) && el.dash.length > 0;
  if (dashed) { try { doc.setLineDashPattern(el.dash, 0); } catch { /* solid */ } }
  doc.line(el.x, el.y, el.x + (el.w || 0), el.y + (el.h || 0));
  if (dashed) { try { doc.setLineDashPattern([], 0); } catch { /* solid */ } }
}

function drawImage(doc, el, ctx) {
  const src = resolveTokens(el.src, { ...STATIC_ASSETS, ...ctx });
  if (!src) return;
  const asset = assetCache.get(src) || (src.startsWith("data:") ? { dataUrl: src, width: 0, height: 0 } : null);
  if (!asset) return; // a static asset still loading is skipped rather than breaking the document
  let { w, h } = el;
  const aspect = asset.width && asset.height ? asset.height / asset.width : 0;
  if (!h && aspect) h = w * aspect;   // only a width given: keep the natural aspect
  if (!h) h = w * 0.4;
  if (el.fit === "contain" && aspect) { // fit inside the box, keeping the aspect ratio
    let rw = w, rh = w * aspect;
    if (rh > h) { rh = h; rw = h / aspect; }
    w = rw; h = rh;
  }
  try {
    doc.addImage(asset.dataUrl, "PNG", el.x, el.y, w, h);
  } catch (e) {
    // The rest of the document is still worth producing, but this should never
    // happen: uploads go through utils/imageFile precisely so an image that
    // cannot be drawn is refused at the form instead of vanishing from here.
    console.error(`layoutEngine: image "${el.src || el.id}" could not be drawn and was left out of the document`, e);
  }
}

// Table rows for a binding, padded with blank rows when the layout asks for a
// fixed ruled grid (`minRows`, like the OnPay stub's 25-line boxes).
function tableRows(el, ctx) {
  const rows = Array.isArray(ctx[el.binding]) ? ctx[el.binding].slice() : [];
  const min = Number(el.minRows) || 0;
  while (rows.length < min) rows.push({});
  return rows;
}

function rowHeightFor(el, row) {
  const base = el.rowHeight || 16;
  const hasSub = (el.columns || []).some((c) => c.sub && resolveTokens(c.sub, row));
  return hasSub ? (el.subRowHeight || Math.round(base * 1.5)) : base;
}

export function measureTable(el, ctx) {
  const rows = tableRows(el, ctx);
  const header = el.showHeader === false ? 0 : (el.headerHeight || el.rowHeight || 16);
  return header + rows.reduce((h, r) => h + rowHeightFor(el, r), 0);
}

// Draws the table; overflowing rows continue on additional pages with the
// header repeated. `pager` manages page switching for the whole render.
// Styling options (all optional): font ("helvetica" | "courier"), showHeader,
// headerHeight, headerFontSize, headerBold, headerStroke, headerColLines,
// minRows, padX / padRight (also per column), textOffset / headerTextOffset
// (text baseline from the row top), zebraStart (0 = first data row shaded),
// underlineFirstCol (true, or a substring to underline), bottomBorder,
// sideBorders, rowLineColor/Width, rowLinesLast, colLineColor/Width,
// colorField (a row field that overrides the text colour), and per column
// `sub` (a second, smaller line under the cell text) with subFontSize.
// Returns the y where the table ends.
function drawTable(doc, el, ctx, pager) {
  const rows = tableRows(el, ctx);
  const cols = el.columns || [];
  if (!cols.length) return el.y;
  const rowH = el.rowHeight || 16;
  const headerH = el.showHeader === false ? 0 : (el.headerHeight || rowH);
  const fontSize = el.fontSize || 8;
  const headerFontSize = el.headerFontSize || fontSize;
  const font = el.font || "helvetica";
  const padX = el.padX ?? 6;
  const padRight = el.padRight ?? padX;
  const bottomLimit = pager.pageHeight - 40;
  const zebraStart = el.zebraStart ?? 1;
  let y = el.y;

  const colX = [];
  let acc = el.x;
  cols.forEach((c) => { colX.push(acc); acc += (c.width || 1 / cols.length) * el.w; });
  const colW = (i) => (cols[i].width || 1 / cols.length) * el.w;

  const cellText = (text, i, col, bold, color, size, rh, offset, align) => {
    doc.setFont(font, bold ? "bold" : "normal");
    doc.setFontSize(size);
    const [r, g, b] = hexToRgb(color);
    doc.setTextColor(r, g, b);
    const baseY = y + (offset ?? (rh / 2 + size * 0.35));
    const a = align || col.align;
    const str = String(text ?? "");
    const left = colX[i] + (col.padX ?? padX);
    if (a === "right") doc.text(str, colX[i] + colW(i) - (col.padRight ?? padRight), baseY, { align: "right" });
    else if (a === "center") doc.text(str, colX[i] + colW(i) / 2, baseY, { align: "center" });
    else doc.text(str, left, baseY);
    return { str, baseY, left };
  };

  const drawColLines = (h) => {
    if (!el.colLines) return;
    const [r, g, b] = hexToRgb(el.colLineColor || "#c8c8c8");
    doc.setDrawColor(r, g, b);
    doc.setLineWidth(el.colLineWidth || 0.3);
    for (let i = 1; i < colX.length; i++) doc.line(colX[i], y, colX[i], y + h);
  };

  const drawHeader = () => {
    if (!headerH) return;
    if (el.headerFill && el.headerFill !== "none") {
      const [r, g, b] = hexToRgb(el.headerFill);
      doc.setFillColor(r, g, b);
      doc.rect(el.x, y, el.w, headerH, "F");
    }
    if (el.headerStroke && el.headerStroke !== "none") {
      const [r, g, b] = hexToRgb(el.headerStroke);
      doc.setDrawColor(r, g, b);
      doc.setLineWidth(el.headerStrokeWidth || 0.5);
      doc.rect(el.x, y, el.w, headerH, "S");
    }
    cols.forEach((c, i) => cellText(c.header || "", i, c, el.headerBold !== false, el.headerColor || "#334155", headerFontSize, headerH, el.headerTextOffset, c.headerAlign));
    if (el.headerColLines !== false) drawColLines(headerH);
    y += headerH;
  };

  const startY = y;
  drawHeader();
  rows.forEach((row, ri) => {
    const rh = rowHeightFor(el, row);
    if (y + rh > bottomLimit) {
      pager.nextOverflowPage();
      y = 40;
      drawHeader();
    }
    if (el.zebra && ri >= zebraStart && (ri - zebraStart) % 2 === 0) {
      const [zr, zg, zb] = hexToRgb(el.zebraFill || "#f8fafc");
      doc.setFillColor(zr, zg, zb);
      doc.rect(el.x, y, el.w, rh, "F");
    }
    const rowColor = (el.colorField && row[el.colorField]) || null;
    cols.forEach((c, i) => {
      const cell = cellText(resolveTokens(c.token, row), i, c, !!c.bold, rowColor || c.color || el.color || "#1a1a1a", fontSize, rh, el.textOffset);
      if (el.underlineFirstCol && i === 0 && cell.str) {
        // Gusto underlines the description; a substring limits it to matching cells
        const part = typeof el.underlineFirstCol === "string" ? el.underlineFirstCol : cell.str;
        if (cell.str.includes(part)) {
          doc.setDrawColor(0, 0, 0);
          doc.setLineWidth(0.3);
          doc.line(cell.left, cell.baseY + 1, cell.left + doc.getTextWidth(part), cell.baseY + 1);
        }
      }
      if (c.sub) {
        const sub = resolveTokens(c.sub, row);
        if (sub) {
          doc.setFont(font, "normal");
          doc.setFontSize(c.subFontSize || el.subFontSize || Math.max(fontSize - 1, 5));
          const [r, g, b] = hexToRgb(c.subColor || rowColor || c.color || el.color || "#1a1a1a");
          doc.setTextColor(r, g, b);
          doc.text(String(sub), cell.left, cell.baseY + (el.subOffset || 8));
        }
      }
    });
    drawColLines(rh);
    const last = ri === rows.length - 1;
    if (el.rowLines !== false && !(last && el.rowLinesLast === false)) {
      const [r, g, b] = hexToRgb(el.rowLineColor || "#e2e8f0");
      doc.setDrawColor(r, g, b);
      doc.setLineWidth(el.rowLineWidth || 0.4);
      doc.line(el.x, y + rh, el.x + el.w, y + rh);
    }
    y += rh;
  });
  if (el.bottomBorder) {
    const [r, g, b] = hexToRgb(el.bottomBorder === true ? "#c8c8c8" : el.bottomBorder);
    doc.setDrawColor(r, g, b);
    doc.setLineWidth(el.bottomBorderWidth || 0.5);
    doc.line(el.x, y, el.x + el.w, y);
  }
  if (el.sideBorders) {
    const [r, g, b] = hexToRgb(el.sideBorders === true ? "#000000" : el.sideBorders);
    doc.setDrawColor(r, g, b);
    doc.setLineWidth(el.sideBorderWidth || 0.5);
    doc.line(el.x, startY, el.x, y);
    doc.line(el.x + el.w, startY, el.x + el.w, y);
  }
  return y;
}

// ── customer accent color ────────────────────────────────────────────────────
// A layout may declare `accentOption: { enabled, baseColor, swatches: [] }`.
// When enabled and the customer picked a color (formData.accentColor), every
// element field that uses baseColor is swapped to the chosen color at render
// time — the designed layout itself is never mutated.
const ACCENT_FIELDS = ["color", "fill", "stroke", "headerFill", "headerColor", "zebraFill"];

function applyAccentColor(layout, chosen) {
  const opt = layout.accentOption;
  if (!opt || !opt.enabled || !chosen) return layout;
  if (!/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(String(chosen))) return layout;
  const base = String(opt.baseColor || "").toLowerCase();
  if (!base) return layout;
  return {
    ...layout,
    elements: (layout.elements || []).map((el) => {
      let swapped = null;
      for (const f of ACCENT_FIELDS) {
        if (String(el[f] || "").toLowerCase() === base) {
          swapped = swapped || { ...el };
          swapped[f] = chosen;
        }
      }
      return swapped || el;
    }),
  };
}

// A colour data field (customFields entry with type "color") lets an element
// paint from a {token}: at render time each colour property that holds "{token}"
// is swapped for the customer's chosen colour, or the field's default. A raw
// {token} must never reach the renderer, so an unresolved one falls back to a
// neutral colour.
const COLOR_TOKEN_RE = /^\s*\{(\w+)\}\s*$/;
const HEX_COLOR_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

export function resolveColorFields(layout, ctx) {
  if (!layout || !Array.isArray(layout.elements)) return layout;
  const defaults = {};
  const fields = layout.meta && Array.isArray(layout.meta.customFields) ? layout.meta.customFields : [];
  for (const f of fields) if (f && f.type === "color" && f.token) defaults[f.token] = f.default;
  const resolveOne = (val) => {
    const m = COLOR_TOKEN_RE.exec(String(val || ""));
    if (!m) return val;
    const chosen = (ctx && ctx[m[1]]) || defaults[m[1]] || "";
    return HEX_COLOR_RE.test(chosen) ? chosen : "#333333";
  };
  let changed = false;
  const elements = layout.elements.map((el) => {
    if (!el) return el;
    let out = null;
    for (const f of ACCENT_FIELDS) {
      if (el[f] && COLOR_TOKEN_RE.test(String(el[f]))) { out = out || { ...el }; out[f] = resolveOne(el[f]); }
    }
    if (Array.isArray(el.columns) && el.columns.some((c) => c && (COLOR_TOKEN_RE.test(String(c.color || "")) || COLOR_TOKEN_RE.test(String(c.subColor || ""))))) {
      out = out || { ...el };
      out.columns = el.columns.map((c) => {
        if (!c) return c;
        let cc = c;
        if (c.color && COLOR_TOKEN_RE.test(String(c.color))) cc = { ...cc, color: resolveOne(c.color) };
        if (c.subColor && COLOR_TOKEN_RE.test(String(c.subColor))) cc = { ...cc, subColor: resolveOne(c.subColor) };
        return cc;
      });
    }
    if (out) changed = true;
    return out || el;
  });
  return changed ? { ...layout, elements } : layout;
}

// How many lines a text element takes, for flow. renderLayout measures with
// jsPDF itself (exact); the editor canvas estimates with a 2D canvas in the
// same family, which is close enough to place the next block.
let estimateCanvas = null;
const FONT_FAMILIES = { courier: "'Courier New', Courier, monospace", times: "'Times New Roman', Times, serif", yellowtail: "Yellowtail, cursive" };

// jsPDF ships helvetica, times and courier; a layout may also use the
// Yellowtail script the generators sign with. Its TTF is fetched once (by
// preloadLayoutAssets, or on first use) and registered on each document it
// draws on; until it has loaded, text in it falls back to italic Times.
const LAYOUT_FONTS = { yellowtail: { family: "Yellowtail", file: "Yellowtail-Regular.ttf", url: "/fonts/Yellowtail-Regular.ttf" } };
const fontBase64 = {};
const fontLoading = {};
export function loadLayoutFont(key) {
  const def = LAYOUT_FONTS[key];
  if (!def) return Promise.resolve(null);
  if (fontBase64[key]) return Promise.resolve(fontBase64[key]);
  if (!fontLoading[key]) {
    fontLoading[key] = fetch(def.url)
      .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(`font ${key} unavailable`))))
      .then((buf) => {
        const bytes = new Uint8Array(buf);
        let bin = "";
        for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
        fontBase64[key] = btoa(bin);
        return fontBase64[key];
      })
      .catch(() => { delete fontLoading[key]; return null; });
  }
  return fontLoading[key];
}
const layoutFonts = (layout) => [...new Set(((layout && layout.elements) || []).map((el) => el && el.font).filter((f) => LAYOUT_FONTS[f]))];
// The same face for the editor canvas (and the line estimate), via FontFace
const browserFonts = {};
export function loadBrowserFont(key) {
  const def = LAYOUT_FONTS[key];
  if (!def || browserFonts[key]) return Promise.resolve();
  if (typeof document === "undefined" || !document.fonts || typeof FontFace === "undefined") return Promise.resolve();
  browserFonts[key] = new FontFace(def.family, `url(${def.url})`).load()
    .then((face) => { document.fonts.add(face); })
    .catch(() => { delete browserFonts[key]; });
  return browserFonts[key];
}
function registerLayoutFonts(doc, layout) {
  const ready = new Set();
  for (const key of layoutFonts(layout)) {
    if (!fontBase64[key]) { loadLayoutFont(key); continue; }
    const def = LAYOUT_FONTS[key];
    try { doc.addFileToVFS(def.file, fontBase64[key]); doc.addFont(def.file, def.family, "normal"); ready.add(key); }
    catch (e) { console.error(`layoutEngine: font ${key} could not be registered`, e); }
  }
  return ready;
}
export const fontFamilyFor = (font) => FONT_FAMILIES[font] || "Helvetica, Arial, sans-serif";
export function estimateTextWrap(el, text) {
  if (!text) return [];
  const paras = String(text).split("\n");
  if (!(el.wrap && el.w)) return paras;
  const size = el.fontSize || 9;
  let c2d = null;
  try {
    if (!estimateCanvas && typeof document !== "undefined") estimateCanvas = document.createElement("canvas");
    c2d = estimateCanvas ? estimateCanvas.getContext("2d") : null;
    if (c2d) c2d.font = `${el.italic ? "italic " : ""}${el.bold ? "bold " : ""}${size}px ${fontFamilyFor(el.font)}`;
  } catch { c2d = null; }
  const width = (str) => (c2d ? c2d.measureText(str).width : str.length * size * 0.5);
  const lines = [];
  for (const para of paras) {
    if (!para.trim()) { lines.push(""); continue; }
    let line = "";
    for (const word of para.split(" ")) {
      const test = line ? `${line} ${word}` : word;
      if (width(test) > el.w && line) { lines.push(line); line = word; } else line = test;
    }
    if (line) lines.push(line);
  }
  return lines;
}
export const estimateTextLines = (el, text) => estimateTextWrap(el, text).length;

function measureWithDoc(doc, el, text) {
  if (!text) return [];
  if (!(el.wrap && el.w)) return String(text).split("\n");
  try {
    setTextStyle(doc, el);
    // jsPDF keeps a blank line for an empty paragraph the way the generators do
    return String(text).split("\n").flatMap((para) => (para.trim() ? doc.splitTextToSize(para, el.w) : [""]));
  } catch { return estimateTextWrap(el, text); }
}

// Elements may sit a fixed distance below another element instead of at an
// absolute y (`after: "<element id>", offset: n`), so a section follows a
// table whose row count varies with the data — the way the built-in
// generators flow. This resolves every element's y for a data context; the
// renderer and the editor canvas both use it, so they agree. Elements hidden
// by showIf take no space, so a chain through a hidden table collapses.
//
// A layout with meta.flow = "baseline" flows the way the letter-style
// generators do: a chained text's `offset` places its first BASELINE below
// the element before it, a text's height is its measured lines x pitch
// (fontSize x lineHeight), `spaceAfter` is the gap an element leaves only
// when it shows, a hidden element passes the chain straight through, and a
// chained element that would run past meta.flowMargins.bottom continues at
// the top of the next page.
export function resolveLayoutPositions(layout, ctx, measure = estimateTextWrap) {
  const list = Array.isArray(layout && layout.elements) ? layout.elements : [];
  const endY = {};
  const baselineFlow = !!(layout && layout.meta && layout.meta.flow === "baseline");
  if (!baselineFlow) {
    return list.map((el) => {
      if (!el) return el;
      let y = el.y;
      if (el.after && endY[el.after] !== undefined) y = endY[el.after] + (Number(el.offset) || 0);
      const placed = y === el.y ? el : { ...el, y };
      let h = 0;
      if (evalShowIf(el.showIf, ctx)) {
        if (el.type === "table") h = measureTable(placed, ctx);
        else if (el.type === "text") h = (el.fontSize || 9) * 1.2;
        else h = el.h || 0;
      }
      if (el.id) endY[el.id] = y + h;
      return placed;
    });
  }
  const margins = (layout.meta && layout.meta.flowMargins) || {};
  const topMargin = Number(margins.top) || 60;
  const bottomMargin = Number(margins.bottom) || 60;
  const pageHeight = (layout.page && layout.page.height) || 792;
  const limit = pageHeight - bottomMargin;
  const pageOf = {};
  const rebased = {}; // anchor id -> { page, end } once an element off it broke to a new page
  return list.flatMap((el) => {
    if (!el) return [el];
    const isText = el.type === "text";
    const size = el.fontSize || 9;
    const pitch = size * (el.lineHeight || 1.15);
    const shown = evalShowIf(el.showIf, ctx);
    const chained = !!(el.after && endY[el.after] !== undefined);
    const offset = Number(el.offset) || 0;
    let y = el.y;
    let page = el.page || 1;
    if (chained) {
      const moved = rebased[el.after];
      const anchor = (moved ? moved.end : endY[el.after]) + offset;
      y = isText ? anchor - 0.85 * size : anchor;
      page = moved ? moved.page : (pageOf[el.after] || page);
    }
    let h = 0;
    let lines = null;
    if (shown) {
      if (el.type === "table") h = measureTable({ ...el, y }, ctx);
      else if (isText) { lines = measure(el, resolveTokens(el.content, ctx)) || []; h = 0.85 * size + lines.length * pitch; }
      else h = el.h || 0;
      // `keep` is the room an element wants below its baseline (the
      // generators' ensureSpace), so a signature block stays together
      const keepFrom = (isText ? y + 0.85 * size : y) + (Number(el.keep) || 0);
      if (chained && y > topMargin && (y + h > limit || keepFrom > limit)) {
        // a paragraph that reaches the margin carries on at the top of the
        // next page, as many lines as still fit staying put
        if (isText && lines && lines.length > 1 && !el.keep) {
          const baseline = y + 0.85 * size;
          const fit = Math.floor((limit - pitch - baseline) / pitch) + 1;
          if (fit >= 1 && fit < lines.length) {
            const tailLines = lines.slice(fit);
            const samePageHead = page === (el.page || 1);
            const head = { ...el, y, content: lines.slice(0, fit).join("\n"), spaceAfter: 0, ...(samePageHead ? {} : { page }) };
            const tail = { ...el, id: `${el.id}~tail`, after: undefined, offset: undefined, keep: undefined, y: topMargin - 0.85 * size, page: page + 1, content: tailLines.join("\n"), _virtual: true };
            if (el.id) { pageOf[el.id] = page + 1; endY[el.id] = topMargin + tailLines.length * pitch + (Number(el.spaceAfter) || 0); }
            return [head, tail];
          }
        }
        page += 1;
        y = (isText ? topMargin - 0.85 * size : topMargin) + offset;
        if (!rebased[el.after]) rebased[el.after] = { page, end: topMargin };
      }
    }
    const samePage = page === (el.page || 1);
    const placed = (y === el.y && samePage) ? el : { ...el, y, ...(samePage ? {} : { page }) };
    if (el.id) {
      pageOf[el.id] = page;
      if (shown) endY[el.id] = y + h + (Number(el.spaceAfter) || 0);
      else endY[el.id] = chained ? endY[el.after] : (isText ? y + 0.85 * size : y);
    }
    return [placed];
  });
}

// Renders a full layout onto a jsPDF document. Elements are grouped by their
// `page` number; pages are appended as needed (including table overflow).
export function renderLayout(doc, rawLayout, templateData, documentType = "paystub", opts = {}) {
  const chosenAccent = (templateData && (templateData.formData?.accentColor || templateData.accentColor)) || "";
  // Neither recolouring nor context-building may throw — a malformed published
  // layout must degrade (fall back to the raw layout / an empty context) rather
  // than fail the whole document generation.
  let layout;
  try { layout = applyAccentColor(rawLayout, chosenAccent); }
  catch (e) { console.error("layoutEngine: applyAccentColor failed, using raw layout", e); layout = rawLayout || {}; }
  // Apply the template's own PDF metadata (title/author/creator/producer…);
  // generators skip their built-in metadata for custom templates.
  if (layout.metadata) {
    const props = {};
    ["title", "subject", "author", "keywords", "creator", "producer"].forEach((k) => {
      if (layout.metadata[k]) props[k] = layout.metadata[k];
    });
    if (Object.keys(props).length) {
      try { doc.setProperties(props); } catch (e) { /* metadata must never break rendering */ }
    }
  }
  let ctx;
  try { ctx = buildContext(templateData, documentType, opts && opts.raw); }
  catch (e) { console.error("layoutEngine: buildContext failed, using empty context", e); ctx = {}; }
  try { doc.__layoutFonts = registerLayoutFonts(doc, layout); } catch { doc.__layoutFonts = new Set(); }
  try { layout = resolveColorFields(layout, ctx); }
  catch (e) { console.error("layoutEngine: resolveColorFields failed", e); }
  const pageHeight = (layout && layout.page && layout.page.height) || 792;
  let elementList;
  try { elementList = resolveLayoutPositions(layout, ctx, (el, text) => measureWithDoc(doc, el, text)); }
  catch (e) { console.error("layoutEngine: resolveLayoutPositions failed, using raw positions", e); elementList = Array.isArray(layout && layout.elements) ? layout.elements : []; }
  const elements = elementList.filter((el) => el && evalShowIf(el.showIf, ctx));

  const maxPage = elements.reduce((m, el) => Math.max(m, el.page || 1), 1);
  const startPage = doc.getNumberOfPages();
  let pagesAdded = 0;

  const pager = {
    pageHeight,
    nextOverflowPage() {
      doc.addPage();
      pagesAdded++;
    },
  };

  for (let p = 1; p <= maxPage; p++) {
    if (p > 1) {
      doc.addPage();
      pagesAdded++;
    }
    // eslint-disable-next-line no-loop-func
    elements.filter((el) => (el.page || 1) === p).forEach((el) => {
      try {
        if (el.type === "rect") drawRect(doc, el);
        else if (el.type === "line") drawLine(doc, el);
        else if (el.type === "image") drawImage(doc, el, ctx);
        else if (el.type === "table") drawTable(doc, el, ctx, pager);
        else drawText(doc, el, ctx);
      } catch (e) {
        // One bad element must never take down the whole document.
        console.error("layoutEngine: element failed to render", el, e);
      }
    });
  }
  // the footer page count (meta.pageNumbers: x, y (baseline), w, fontSize,
  // color, font, align, format with {page} and {pages})
  const pn = layout && layout.meta && layout.meta.pageNumbers;
  if (pn && pn.format) {
    const total = pagesAdded + 1;
    const fs = pn.fontSize || 8.5;
    for (let i = 0; i < total; i++) {
      try {
        doc.setPage(startPage + i);
        drawText(doc, {
          type: "text", x: pn.x || 0, y: (pn.y || pageHeight - 30) - 0.85 * fs, w: pn.w || 100, fontSize: fs,
          color: pn.color || "#8c8c8c", font: pn.font, align: pn.align,
          content: String(pn.format).replace("{page}", String(i + 1)).replace("{pages}", String(total)),
        }, ctx);
      } catch (e) { console.error("layoutEngine: page number failed", e); }
    }
    doc.setPage(startPage + total - 1);
  }
  return { startPage, pagesAdded };
}

// ── published-layout fetching (used by the public generators) ────────────────

// Cached with a short TTL: the cache exists so debounced previews don't
// refetch on every keystroke, but a republished design must reach long-lived
// SPA sessions (including the admin's own tab navigating from the editor to
// the user-facing pages) without a hard reload. Pinned-version fetches are
// immutable and cached forever.
const layoutCache = new Map();
const LAYOUT_CACHE_TTL = 60 * 1000;

export async function fetchPublishedLayout(templateId, version, opts = {}) {
  const key = (opts.user ? "u:" : "") + (version ? `${templateId}@${version}` : templateId);
  const hit = layoutCache.get(key);
  if (hit && (version || Date.now() - hit.at < LAYOUT_CACHE_TTL)) return hit.layout;
  try {
    // The timestamp busts any intermediary HTTP cache; the Map above already
    // limits this to one network request per template per TTL window.
    const q = version ? `?version=${version}` : `?_=${Date.now()}`;
    // A business's own template serves from the authed user endpoint.
    const base = opts.user
      ? `${BACKEND_URL}/api/user/doc-templates/${templateId}/layout`
      : `${BACKEND_URL}/api/doc-templates/${templateId}/layout`;
    const res = await fetch(base + q, opts.user ? { headers: userHeaders() } : undefined);
    if (!res.ok) return hit ? hit.layout : null;
    const data = await res.json();
    const layout = data.layout || null;
    layoutCache.set(key, { layout, at: Date.now() });
    return layout;
  } catch {
    return hit ? hit.layout : null;
  }
}

export function clearLayoutCache() {
  layoutCache.clear();
}

// Puts a layout in the cache under a generator template string, keyed as
// fetchPublishedLayout keys it, so the stub preview generators (which fetch
// by that string) find it without a request: the admin's preview of a
// business's template serves the layout from the admin endpoint.
export function primeLayoutCache(template, layout) {
  const ref = parseCustomTemplate(template);
  if (!ref) return;
  layoutCache.set((ref.user ? "u:" : "") + ref.id, { layout, at: Date.now() });
}

// A generator template string is either a built-in id ("template-a"), an
// admin-published custom template ("custom:<id>"), or a business's own
// published template ("usercustom:<id>"). The last serves from the authed
// user endpoint. Returns {id, user} for a custom template, or null.
export function parseCustomTemplate(template) {
  if (typeof template !== "string") return null;
  if (template.startsWith("usercustom:")) return { id: template.slice(11), user: true };
  if (template.startsWith("custom:")) return { id: template.slice(7), user: false };
  return null;
}

export const isCustomTemplate = (template) => !!parseCustomTemplate(template);

// A template can define its own data fields (layout.meta.customFields): each is
// { token, label, category, description, type }. This turns them into insert-
// menu token groups, grouped by category, so they sit beside the built-ins.
export function customFieldGroups(layout) {
  const fields = layout && layout.meta && Array.isArray(layout.meta.customFields) ? layout.meta.customFields : [];
  const groups = [];
  for (const f of fields) {
    if (!f || !f.token) continue;
    const cat = (String(f.category || "").trim()) || "Custom fields";
    let g = groups.find((x) => x.group === cat);
    if (!g) { g = { group: cat, tokens: [] }; groups.push(g); }
    g.tokens.push([`{${f.token}}`, f.label || f.token]);
  }
  return groups;
}

// The custom fields a template defines (safe accessor).
export function getCustomFields(layout) {
  return layout && layout.meta && Array.isArray(layout.meta.customFields) ? layout.meta.customFields : [];
}

// Fetch the published layout behind a "custom:"/"usercustom:" template string,
// hitting the right (public or authed) endpoint.
export function fetchCustomLayout(template) {
  const ref = parseCustomTemplate(template);
  return ref ? fetchPublishedLayout(ref.id, undefined, { user: ref.user }) : Promise.resolve(null);
}

// The paystub table bindings a paystub design carries; their presence marks a
// layout as a computed pay stub (which the token form can't drive).
const PAYSTUB_BINDINGS = new Set([
  "earnings", "employeetaxes", "employertaxes", "deductions", "deductionitems",
  "contributionitems", "onpayearnings", "onpaytaxes", "onpaydeductions", "summary",
]);
const US_ONLY_BINDINGS = new Set(["employeetaxes", "employertaxes", "onpaytaxes", "onpayearnings", "contributionitems"]);

// Which built-in a stored layout was designed from — "paystub",
// "canadian-paystub", or "" (a free-form document). An explicit
// layout.meta.baseDocType (stamped at creation) wins; otherwise we sniff the
// paystub table bindings, and tell the Canadian Classic stub from the US one by
// its "Statement of Earnings" title (their layouts are otherwise identical).
export function inferBaseDocType(layout) {
  if (!layout || typeof layout !== "object") return "";
  const meta = layout.meta && layout.meta.baseDocType;
  if (meta === "paystub" || meta === "canadian-paystub" || meta === "uk-payslip") return meta;
  const els = Array.isArray(layout.elements) ? layout.elements : [];
  const bindings = els.map((e) => (e && e.type === "table" ? String(e.binding || "").toLowerCase() : ""));
  const hasPaystubBinding = bindings.some((b) => PAYSTUB_BINDINGS.has(b));
  let s = "";
  try { s = JSON.stringify(layout).toLowerCase(); } catch { s = ""; }
  const hasPaystubToken = /\{grosspay\}|\{netpay\}|\{ytdgrosspay\}|\{federaltax\}/.test(s);
  if (!hasPaystubBinding && !hasPaystubToken) return "";
  if (s.includes("{payetax}") || s.includes("{nationalinsurance}") || s.includes("national insurance")) return "uk-payslip";
  const hasUsOnly = bindings.some((b) => US_ONLY_BINDINGS.has(b));
  if (!hasUsOnly && s.includes("statement of earnings")) return "canadian-paystub";
  return "paystub";
}

// ── token catalogs (drive the editor's insert menus) ─────────────────────────

const PAYSTUB_TOKEN_GROUPS = [
  {
    group: "Company",
    tokens: [
      ["{company}", "Company name"], ["{companyAddress}", "Company address"],
      ["{companyCityStateZip}", "Company city/state/zip"], ["{companyPhone}", "Company phone"],
    ],
  },
  {
    group: "Employee",
    tokens: [
      ["{employeeName}", "Employee name"], ["{employeeAddress}", "Employee address"],
      ["{employeeCityStateZip}", "Employee city/state/zip"], ["{ssn}", "SSN (masked)"],
      ["{employeeId}", "Employee ID"], ["{filingStatus}", "Filing status"],
      ["{workerLabel}", "EMPLOYEE / CONTRACTOR"],
    ],
  },
  {
    group: "Pay period",
    tokens: [
      ["{payPeriod}", "Pay period range"], ["{startDate}", "Period start"], ["{endDate}", "Period end"],
      ["{payDate}", "Pay date"], ["{payFrequency}", "Pay frequency"], ["{stubLabel}", "Stub # of #"],
    ],
  },
  {
    group: "Amounts",
    tokens: [
      ["{grossPay}", "Gross pay"], ["{netPay}", "Net pay"], ["{regularPay}", "Regular pay"],
      ["{overtimePay}", "Overtime pay"], ["{rate}", "Hourly rate"], ["{hours}", "Hours"],
      ["{totalTax}", "Total taxes"], ["{totalDeductions}", "Total deductions"],
      ["{federalTax}", "Federal tax"], ["{stateTax}", "State tax"], ["{ssTax}", "Social Security"],
      ["{medTax}", "Medicare"],
    ],
  },
  {
    group: "Year to date",
    tokens: [
      ["{ytdGrossPay}", "YTD gross"], ["{ytdNetPay}", "YTD net"], ["{ytdTotalTax}", "YTD taxes"],
      ["{ytdHours}", "YTD hours"],
    ],
  },
];

const CANADIAN_EXTRA_GROUP = {
  group: "Canadian",
  tokens: [
    ["{cpp}", "CPP/QPP amount"], ["{ei}", "EI amount"], ["{qpip}", "QPIP amount"],
    ["{provincialTax}", "Provincial tax"], ["{cppLabel}", "CPP/QPP label"],
    ["{ytdCpp}", "YTD CPP/QPP"], ["{ytdEi}", "YTD EI"], ["{ytdProvincialTax}", "YTD provincial tax"],
  ],
};

const UK_EXTRA_GROUP = {
  group: "UK",
  tokens: [
    ["{payeTax}", "PAYE income tax"], ["{nationalInsurance}", "National Insurance"], ["{studentLoan}", "Student loan"],
    ["{taxCode}", "Tax code"], ["{niNumber}", "NI number"],
    ["{ytdPayeTax}", "YTD PAYE"], ["{ytdNationalInsurance}", "YTD National Insurance"], ["{ytdStudentLoan}", "YTD student loan"],
  ],
};

const OFFER_LETTER_TOKEN_GROUPS = [
  {
    group: "Company",
    tokens: [
      ["{companyName}", "Company name"], ["{companyAddress}", "Company address"],
      ["{companyCityStateZip}", "Company city/state/zip"], ["{companyPhone}", "Company phone"],
      ["{companyEmail}", "Company email"],
    ],
  },
  {
    group: "Candidate",
    tokens: [
      ["{candidateName}", "Candidate name"], ["{candidateAddress}", "Candidate address"],
      ["{candidateCityStateZip}", "Candidate city/state/zip"],
    ],
  },
  {
    group: "Offer",
    tokens: [
      ["{jobTitle}", "Job title"], ["{department}", "Department"], ["{employmentType}", "Employment type"],
      ["{workLocation}", "Work location"], ["{startDate}", "Start date"], ["{compensation}", "Compensation"],
      ["{payFrequency}", "Pay frequency"], ["{benefits}", "Benefits (multiline)"],
      ["{additionalTerms}", "Additional terms"], ["{responseDeadline}", "Response deadline"],
    ],
  },
  {
    group: "Letter",
    tokens: [
      ["{letterDate}", "Letter date"], ["{signerName}", "Signer name"], ["{signerTitle}", "Signer title"],
    ],
  },
];

const LEGAL_DOCUMENT_TOKEN_GROUPS = [
  {
    group: "Document",
    tokens: [
      ["{documentTitle}", "Document title"], ["{effectiveDate}", "Effective date"],
      ["{governingState}", "Governing state"], ["{todayDate}", "Today's date"],
    ],
  },
  {
    group: "Party A",
    tokens: [
      ["{partyAName}", "Party A name"], ["{partyATitle}", "Party A title"],
      ["{partyAAddress}", "Party A address"], ["{partyACityStateZip}", "Party A city/state/zip"],
      ["{partyAEmail}", "Party A email"], ["{partyAPhone}", "Party A phone"],
    ],
  },
  {
    group: "Party B",
    tokens: [
      ["{partyBName}", "Party B name"], ["{partyBTitle}", "Party B title"],
      ["{partyBAddress}", "Party B address"], ["{partyBCityStateZip}", "Party B city/state/zip"],
      ["{partyBEmail}", "Party B email"], ["{partyBPhone}", "Party B phone"],
    ],
  },
  {
    group: "Content",
    tokens: [
      ["{recitals}", "Recitals (multiline)"], ["{terms}", "Terms (multiline)"],
      ["{additionalTerms}", "Additional terms"],
    ],
  },
  {
    group: "Signatures",
    tokens: [
      ["{partyASignature}", "Party A drawn signature (image src)"], ["{partyASignatureName}", "Party A typed signature"],
      ["{partyASignDate}", "Party A sign date"],
      ["{partyBSignature}", "Party B drawn signature (image src)"], ["{partyBSignatureName}", "Party B typed signature"],
      ["{partyBSignDate}", "Party B sign date"],
      ["{notaryState}", "Notary state"], ["{notaryCounty}", "Notary county"],
    ],
  },
];

const RESUME_TOKEN_GROUPS = [
  {
    group: "Contact",
    tokens: [
      ["{fullName}", "Full name"], ["{contactLine}", "Contact line (joined)"],
      ["{email}", "Email"], ["{phone}", "Phone"], ["{location}", "Location"],
      ["{linkedin}", "LinkedIn"], ["{website}", "Website"],
    ],
  },
  {
    group: "Sections",
    tokens: [
      ["{summary}", "Professional summary"], ["{skillsLine}", "Skills (joined)"],
      ["{experienceText}", "Experience (full text block)"], ["{educationText}", "Education (full text block)"],
    ],
  },
];

const PAYSTUB_TABLE_BINDINGS = [
  { binding: "earnings", label: "Earnings rows", rowTokens: ["{name}", "{nameDetailed}", "{rate}", "{rateDetailed}", "{hours}", "{current}", "{ytd}"] },
  { binding: "deductions", label: "Deduction rows (taxes + items)", rowTokens: ["{name}", "{current}", "{ytd}"] },
  { binding: "employeeTaxes", label: "Employee taxes", rowTokens: ["{name}", "{current}", "{ytd}"] },
  { binding: "employerTaxes", label: "Employer taxes", rowTokens: ["{name}", "{current}", "{ytd}"] },
  { binding: "deductionItems", label: "Deduction items (with tax type)", rowTokens: ["{name}", "{taxType}", "{current}", "{ytd}"] },
  { binding: "contributionItems", label: "Contribution items (with tax type)", rowTokens: ["{name}", "{taxType}", "{current}", "{ytd}"] },
  { binding: "summary", label: "Summary rows", rowTokens: ["{name}", "{current}", "{ytd}"] },
];

const OFFER_LETTER_TABLE_BINDINGS = [
  { binding: "offerDetails", label: "Offer detail rows", rowTokens: ["{label}", "{value}"] },
];

const LEGAL_DOCUMENT_TABLE_BINDINGS = [
  { binding: "parties", label: "Party rows", rowTokens: ["{role}", "{name}", "{address}", "{cityStateZip}"] },
];

const RESUME_TABLE_BINDINGS = [
  { binding: "experience", label: "Experience rows", rowTokens: ["{position}", "{company}", "{location}", "{companyLine}", "{dates}"] },
  { binding: "education", label: "Education rows", rowTokens: ["{degree}", "{institution}", "{institutionLine}", "{gpa}", "{date}"] },
  { binding: "skillRows", label: "Skill rows", rowTokens: ["{name}"] },
];

const BANK_STATEMENT_TOKEN_GROUPS = [
  { group: "Bank & Period", tokens: [
    ["{bankName}", "Bank name"],
    ["{logoDataUrl}", "Bank logo image"],
    ["{monthText}", "Statement month (August 2026)"],
    ["{dateRange}", "Statement period range"],
    ["{statementStart}", "Period start date"],
    ["{statementEnd}", "Period end date"],
  ]},
  { group: "Account Holder", tokens: [
    ["{accountName}", "Account holder name"],
    ["{accountAddress1}", "Address line 1"],
    ["{accountAddress2}", "Address line 2 (city, state ZIP)"],
    ["{accountNumber}", "Account number"],
    ["{accountNumberMasked}", "Masked account number (••••1234)"],
  ]},
  { group: "Summary", tokens: [
    ["{beginningBalance}", "Beginning balance"],
    ["{endingBalance}", "Ending balance"],
    ["{totalDeposits}", "Total deposits"],
    ["{totalPurchases}", "Total purchases"],
    ["{totalWithdrawals}", "Total withdrawals"],
    ["{totalTransfers}", "Total transfers"],
    ["{totalRefunds}", "Total refunds"],
    ["{transactionCount}", "Number of transactions"],
  ]},
];

const BANK_STATEMENT_TABLE_BINDINGS = [
  { binding: "transactions", label: "Transaction rows", rowTokens: ["{date}", "{description}", "{type}", "{amount}", "{signedAmount}", "{balance}"] },
  { binding: "summaryRows", label: "Summary rows", rowTokens: ["{label}", "{value}"] },
];

// Blank canvas: every category's fields, with the group names prefixed by
// their category so the Data fields panel stays organized.
const prefixGroups = (category, groups) => groups.map((g) => ({ ...g, group: `${category} · ${g.group}` }));

export function getTokenGroups(documentType) {
  if (FORM_DOC_TYPES[documentType]) return FORM_DOC_TYPES[documentType].tokenGroups;
  if (documentType === "offer-letter") return OFFER_LETTER_TOKEN_GROUPS;
  if (documentType === "legal-document") return LEGAL_DOCUMENT_TOKEN_GROUPS;
  if (documentType === "resume") return RESUME_TOKEN_GROUPS;
  if (documentType === "bank-statement") return BANK_STATEMENT_TOKEN_GROUPS;
  if (documentType === "canadian-paystub") return [...PAYSTUB_TOKEN_GROUPS, CANADIAN_EXTRA_GROUP];
  if (documentType === "uk-payslip") return [...PAYSTUB_TOKEN_GROUPS, UK_EXTRA_GROUP];
  if (documentType === "blank") {
    return [
      ...prefixGroups("Pay Stub", PAYSTUB_TOKEN_GROUPS),
      ...prefixGroups("Canadian Pay Stub", [CANADIAN_EXTRA_GROUP]),
      ...prefixGroups("UK Payslip", [UK_EXTRA_GROUP]),
      ...prefixGroups("Offer Letter", OFFER_LETTER_TOKEN_GROUPS),
      ...prefixGroups("Legal Document", LEGAL_DOCUMENT_TOKEN_GROUPS),
      ...prefixGroups("Resume", RESUME_TOKEN_GROUPS),
      ...prefixGroups("Accounting Mockup", BANK_STATEMENT_TOKEN_GROUPS),
      ...FORM_DOC_KEYS.flatMap((t) => prefixGroups(FORM_DOC_TYPES[t].name, FORM_DOC_TYPES[t].tokenGroups)),
    ];
  }
  return PAYSTUB_TOKEN_GROUPS;
}

export function getTableBindings(documentType) {
  if (FORM_DOC_TYPES[documentType]) return FORM_DOC_TYPES[documentType].tableBindings;
  if (documentType === "offer-letter") return OFFER_LETTER_TABLE_BINDINGS;
  if (documentType === "legal-document") return LEGAL_DOCUMENT_TABLE_BINDINGS;
  if (documentType === "resume") return RESUME_TABLE_BINDINGS;
  if (documentType === "bank-statement") return BANK_STATEMENT_TABLE_BINDINGS;
  if (documentType === "blank") {
    const tag = (category, bindings) => bindings.map((b) => ({ ...b, label: `${category} · ${b.label}` }));
    return [
      ...tag("Pay Stub", PAYSTUB_TABLE_BINDINGS),
      ...tag("Offer Letter", OFFER_LETTER_TABLE_BINDINGS),
      ...tag("Legal Document", LEGAL_DOCUMENT_TABLE_BINDINGS),
      ...tag("Resume", RESUME_TABLE_BINDINGS),
      ...tag("Accounting Mockup", BANK_STATEMENT_TABLE_BINDINGS),
    ];
  }
  return PAYSTUB_TABLE_BINDINGS;
}

// "Show when" presets offered in the editor per document type.
export function getShowIfPresets(documentType) {
  if (FORM_DOC_TYPES[documentType]) return FORM_DOC_TYPES[documentType].showIf;
  if (documentType === "blank") {
    // Union of every category's presets, deduped by condition
    const seen = new Set();
    const all = [];
    for (const t of ["paystub", "offer-letter", "legal-document", "resume", "bank-statement", ...FORM_DOC_KEYS]) {
      for (const [value, label] of getShowIfPresets(t)) {
        if (seen.has(value)) continue;
        seen.add(value);
        all.push([value, label]);
      }
    }
    return all;
  }
  if (documentType === "offer-letter") {
    return [
      ["", "Always"],
      ["hasLogo", "Has company logo"],
      ["hasBenefits", "Has benefits text"],
      ["hasDeadline", "Has response deadline"],
    ];
  }
  if (documentType === "legal-document") {
    return [
      ["", "Always"],
      ["hasRecitals", "Has recitals"],
      ["hasAdditionalTerms", "Has additional terms"],
      ["hasGoverningState", "Has governing state"],
      ["hasPartyASignatureImage", "Party A drew/uploaded signature"],
      ["!hasPartyASignatureImage", "Party A typed signature"],
      ["hasPartyBSignatureImage", "Party B drew/uploaded signature"],
      ["!hasPartyBSignatureImage", "Party B typed signature"],
    ];
  }
  if (documentType === "resume") {
    return [
      ["", "Always"],
      ["hasSummary", "Has summary"],
      ["hasSkills", "Has skills"],
      ["hasLinkedin", "Has LinkedIn"],
      ["hasWebsite", "Has website"],
    ];
  }
  if (documentType === "bank-statement") {
    return [
      ["", "Always"],
      ["hasLogo", "Has bank logo"],
      ["hasTransactions", "Has transactions"],
    ];
  }
  return [
    ["", "Always"],
    ["!isContractor", "Employees only"],
    ["isContractor", "Contractors only"],
    ["payType=salary", "Salaried only"],
    ["payType=hourly", "Hourly only"],
    ["hasLogo", "Has company logo"],
    ["hasOvertime", "Has overtime"],
  ];
}

// ── sample data (editor preview + test renders) ──────────────────────────────

const BASE_SAMPLE = {
  formData: {
    company: "Acme Industries LLC",
    companyAddress: "1200 Commerce Street, Suite 400",
    companyCity: "Dallas", companyState: "TX", companyZip: "75201",
    companyPhone: "(214) 555-0182",
    name: "Jordan Michaels",
    address: "48 Maplewood Drive",
    city: "Dallas", state: "TX", zip: "75218",
    ssn: "123-45-6789",
    federalFilingStatus: "Single",
  },
  payType: "hourly", workerType: "employee", isContractor: false,
  rate: 28.5, hours: 80, overtime: 6,
  regularPay: 2280, overtimePay: 256.5, commission: 0, tips: 0,
  grossPay: 2536.5,
  federalTax: 304.38, stateTax: 0, localTax: 0, ssTax: 157.26, medTax: 36.78,
  totalTax: 498.42, totalDeductions: 75, totalContributions: 0,
  netPay: 1963.08,
  deductionsData: [{ name: "Health Insurance", currentAmount: 75 }],
  contributionsData: [],
  startDate: "2026-08-16", endDate: "2026-08-31", payDate: "2026-09-05",
  payFrequency: "Bi-Weekly", stubNum: 1, totalStubs: 1, ytdPayPeriods: 17,
  ytdRegularPay: 38760, ytdOvertimePay: 4360.5, ytdCommission: 0, ytdTips: 0,
  ytdGrossPay: 43120.5, ytdFederalTax: 5174.46, ytdStateTax: 0, ytdLocalTax: 0,
  ytdSsTax: 2673.47, ytdMedTax: 625.25, ytdTotalTax: 8473.18, ytdNetPay: 33372.32,
  ytdHours: 1462, logoDataUrl: "",
};

export const SAMPLE_TEMPLATE_DATA = BASE_SAMPLE;

const OFFER_LETTER_SAMPLE = {
  formData: {
    companyName: "Acme Industries LLC",
    companyAddress: "1200 Commerce Street, Suite 400",
    companyCity: "Dallas", companyState: "TX", companyZip: "75201",
    companyPhone: "(214) 555-0182", companyEmail: "hr@acmeindustries.com",
    letterDate: "2026-09-03",
    candidateName: "Jordan Michaels",
    candidateAddress: "48 Maplewood Drive",
    candidateCity: "Dallas", candidateState: "TX", candidateZip: "75218",
    jobTitle: "Senior Operations Analyst",
    department: "Operations",
    employmentType: "full-time",
    workLocation: "hybrid",
    startDate: "2026-10-01",
    reportingManager: "Casey Nguyen", reportingTitle: "Director of Operations",
    compensationType: "annual", compensationAmount: 92500, payFrequency: "bi-weekly",
    benefits: "Medical, dental, and vision coverage\n401(k) with 4% company match\n15 days paid time off",
    additionalTerms: "This offer is contingent on successful completion of a background check.",
    responseDeadline: "2026-09-17",
    signerName: "Alex Rivera", signerTitle: "Head of People",
    companyLogo: "",
  },
};

const LEGAL_DOCUMENT_SAMPLE = {
  formData: {
    documentTitle: "Mutual Non-Disclosure Agreement",
    effectiveDate: "2026-09-06",
    governingState: "Texas",
    partyAName: "Jordan Michaels", partyATitle: "Founder",
    partyAAddress: "48 Maplewood Drive", partyACity: "Dallas", partyAState: "TX", partyAZip: "75218",
    partyAEmail: "jordan@example.com", partyAPhone: "(214) 555-0148",
    partyBName: "Casey Nguyen", partyBTitle: "Consultant",
    partyBAddress: "1200 Commerce Street, Suite 400", partyBCity: "Dallas", partyBState: "TX", partyBZip: "75201",
    partyBEmail: "casey@example.com", partyBPhone: "(214) 555-0182",
    recitals: "WHEREAS, the parties wish to explore a potential business relationship; and\nWHEREAS, in connection with that relationship each party may disclose confidential information to the other;",
    terms: "1. Confidential Information. Each party agrees to hold the other party's confidential information in strict confidence and not to disclose it to any third party.\n\n2. Term. The obligations under this Agreement shall remain in effect for a period of three (3) years from the Effective Date.\n\n3. Return of Materials. Upon written request, each party shall promptly return or destroy all confidential materials received from the other party.",
    additionalTerms: "This Agreement may be executed in counterparts, each of which shall be deemed an original.",
    partyASignatureName: "Jordan Michaels", partyASignDate: "2026-09-06",
    partyBSignatureName: "Casey Nguyen", partyBSignDate: "2026-09-06",
  },
};

const RESUME_SAMPLE = {
  formData: {
    personalInfo: {
      fullName: "Jordan Michaels",
      email: "jordan.michaels@example.com", phone: "(214) 555-0148",
      location: "Dallas, TX", linkedin: "linkedin.com/in/jordanmichaels", website: "",
    },
    professionalSummary: "Operations analyst with 7+ years of experience turning messy data into clear decisions. Led cross-functional projects that cut fulfillment costs 18% while improving on-time delivery.",
    optimizedSkills: {
      technical: ["SQL", "Excel / Power Query", "Tableau", "Python"],
      soft: ["Stakeholder communication", "Process design"],
      other: [],
    },
    optimizedExperience: [
      {
        position: "Senior Operations Analyst", company: "Acme Industries LLC", location: "Dallas, TX",
        startDate: "2022-03", endDate: "", current: true,
        bullets: [
          "Built the weekly S&OP dashboard used by 40+ managers, replacing three manual reports",
          "Cut fulfillment costs 18% by redesigning the carrier selection process",
          "Mentored two junior analysts through their first automation projects",
        ],
      },
      {
        position: "Operations Analyst", company: "Lonestar Logistics", location: "Fort Worth, TX",
        startDate: "2019-01", endDate: "2022-02", current: false,
        bullets: [
          "Automated daily volume forecasting, saving 10 hours per week",
          "Ran root-cause analysis that reduced mis-ships by 32%",
        ],
      },
    ],
    education: [
      { degree: "B.B.A.", field: "Supply Chain Management", institution: "University of North Texas", gpa: "3.7", graduationDate: "2018-12" },
    ],
  },
};

// Variant samples the editor can switch between to test conditionals.
const BANK_STATEMENT_SAMPLE = {
  formData: {
    accountName: "John Smith",
    accountAddress1: "123 Main Street",
    accountAddress2: "New York, NY 10001",
    accountNumber: "8834",
    selectedMonth: "2026-08",
    beginningBalance: "2450.00",
    bankName: "Chime",
    bankLogo: "",
    transactions: [
      { date: "2026-08-01", description: "Payroll Deposit: Acme Corp", type: "Deposit", amount: "1963.08" },
      { date: "2026-08-03", description: "Whole Foods Market", type: "Purchase", amount: "86.42" },
      { date: "2026-08-07", description: "Shell Gas Station", type: "Purchase", amount: "48.10" },
      { date: "2026-08-12", description: "Netflix.com", type: "Purchase", amount: "15.49" },
      { date: "2026-08-15", description: "Payroll Deposit: Acme Corp", type: "Deposit", amount: "1963.08" },
      { date: "2026-08-21", description: "Con Edison Utility", type: "Purchase", amount: "134.75" },
      { date: "2026-08-26", description: "ATM Withdrawal", type: "Withdrawal", amount: "100.00" },
    ],
  },
};

export function getSampleVariants(documentType) {
  if (FORM_DOC_TYPES[documentType]) {
    const def = FORM_DOC_TYPES[documentType];
    return [{ key: "default", label: def.sampleLabel || "Sample data", data: def.sample }];
  }
  if (documentType === "blank") {
    // buildContext("blank") merges every category's sample, so one variant
    // covers all fields.
    return [{ key: "default", label: "Sample data (all categories)", data: {} }];
  }
  if (documentType === "offer-letter") {
    return [{ key: "default", label: "Sample offer", data: OFFER_LETTER_SAMPLE }];
  }
  if (documentType === "bank-statement") {
    return [{ key: "default", label: "Sample statement", data: BANK_STATEMENT_SAMPLE }];
  }
  if (documentType === "legal-document") {
    const unsigned = {
      formData: {
        ...LEGAL_DOCUMENT_SAMPLE.formData,
        partyASignatureName: "", partyASignDate: "", partyBSignatureName: "", partyBSignDate: "",
        partyAName: LEGAL_DOCUMENT_SAMPLE.formData.partyAName,
      },
    };
    return [
      { key: "signed", label: "Typed signatures", data: LEGAL_DOCUMENT_SAMPLE },
      { key: "unsigned", label: "Before signing", data: unsigned },
    ];
  }
  if (documentType === "resume") {
    return [{ key: "default", label: "Sample resume", data: RESUME_SAMPLE }];
  }
  const salary = {
    ...BASE_SAMPLE,
    payType: "salary", annualSalary: 88000,
    rate: 0, hours: 0, overtime: 0,
    regularPay: 3384.62, overtimePay: 0, grossPay: 3384.62,
    federalTax: 406.15, ssTax: 209.85, medTax: 49.08, totalTax: 665.08,
    netPay: 2644.54,
  };
  const contractor = {
    ...BASE_SAMPLE,
    workerType: "contractor", isContractor: true,
    federalTax: 0, ssTax: 0, medTax: 0, stateTax: 0, totalTax: 0,
    totalDeductions: 0, deductionsData: [],
    netPay: 2536.5,
  };
  const variants = [
    { key: "hourly", label: "Hourly employee", data: BASE_SAMPLE },
    { key: "salary", label: "Salaried employee", data: salary },
    { key: "contractor", label: "Contractor", data: contractor },
  ];
  if (documentType === "canadian-paystub") {
    return variants.map((v) => ({
      ...v,
      data: {
        ...v.data,
        cpp: 145.32, ei: 41.35, qpip: 0, provincialTax: 128.4, cppLabel: "CPP",
        ytdCpp: 2470.44, ytdEi: 702.95, ytdQpip: 0, ytdProvincialTax: 2182.8,
        formData: { ...v.data.formData, companyCity: "Toronto", companyState: "ON", city: "Toronto", state: "ON" },
      },
    }));
  }
  if (documentType === "uk-payslip") {
    return variants.map((v) => ({
      ...v,
      data: {
        ...v.data,
        payeTax: 312.4, nationalInsurance: 158.6, studentLoan: 0, taxCode: "1257L", niNumber: "AB 12 34 56 C",
        ytdPayeTax: 5310.8, ytdNationalInsurance: 2696.2, ytdStudentLoan: 0,
        formData: { ...v.data.formData, companyCity: "Manchester", companyState: "", city: "Manchester", state: "", taxCode: "1257L", niNumber: "AB 12 34 56 C" },
      },
    }));
  }
  return variants;
}

// ── starter layouts (seeds for new templates) ────────────────────────────────

export const DEFAULT_PAYSTUB_LAYOUT = {
  page: { width: 612, height: 792 },
  elements: [
    // Header band
    { id: "hdr-bg", type: "rect", x: 40, y: 40, w: 532, h: 64, fill: "#14532d", stroke: "none", radius: 4 },
    { id: "hdr-logo", type: "image", x: 56, y: 50, w: 44, h: 44, src: "{logoDataUrl}", showIf: "hasLogo" },
    { id: "hdr-company", type: "text", x: 56, y: 52, w: 300, content: "{company}", fontSize: 15, bold: true, color: "#ffffff" },
    { id: "hdr-addr", type: "text", x: 56, y: 72, w: 300, content: "{companyAddress}", fontSize: 8, color: "#bbf7d0" },
    { id: "hdr-csz", type: "text", x: 56, y: 84, w: 300, content: "{companyCityStateZip}", fontSize: 8, color: "#bbf7d0" },
    { id: "hdr-title", type: "text", x: 372, y: 52, w: 184, content: "EARNINGS STATEMENT", fontSize: 10, bold: true, color: "#ffffff", align: "right" },
    { id: "hdr-period", type: "text", x: 372, y: 70, w: 184, content: "Pay Period: {payPeriod}", fontSize: 8, color: "#bbf7d0", align: "right" },
    { id: "hdr-paydate", type: "text", x: 372, y: 82, w: 184, content: "Pay Date: {payDate}", fontSize: 8, color: "#bbf7d0", align: "right" },

    // Employee block
    { id: "emp-label", type: "text", x: 40, y: 122, w: 200, content: "{workerLabel}", fontSize: 7, bold: true, color: "#64748b" },
    { id: "emp-name", type: "text", x: 40, y: 134, w: 240, content: "{employeeName}", fontSize: 10, bold: true, color: "#1a1a1a" },
    { id: "emp-addr", type: "text", x: 40, y: 148, w: 240, content: "{employeeAddress}", fontSize: 8, color: "#475569" },
    { id: "emp-csz", type: "text", x: 40, y: 160, w: 240, content: "{employeeCityStateZip}", fontSize: 8, color: "#475569" },
    { id: "emp-ssn", type: "text", x: 320, y: 134, w: 252, content: "SSN: {ssn}", fontSize: 8, color: "#475569", align: "right" },
    { id: "emp-freq", type: "text", x: 320, y: 146, w: 252, content: "Pay Frequency: {payFrequency}", fontSize: 8, color: "#475569", align: "right" },
    { id: "emp-filing", type: "text", x: 320, y: 158, w: 252, content: "Filing Status: {filingStatus}", fontSize: 8, color: "#475569", align: "right", showIf: "!isContractor" },
    { id: "div-1", type: "line", x: 40, y: 176, w: 532, h: 0, color: "#e2e8f0", lineWidth: 0.75 },

    // Earnings table
    { id: "earn-label", type: "text", x: 40, y: 190, w: 200, content: "EARNINGS", fontSize: 8, bold: true, color: "#14532d" },
    {
      id: "earn-table", type: "table", x: 40, y: 204, w: 532, binding: "earnings",
      rowHeight: 18, fontSize: 8, headerFill: "#f1f5f9", headerColor: "#334155", color: "#1a1a1a", zebra: false, rowLines: true,
      columns: [
        { header: "Description", token: "{name}", width: 0.34, align: "left" },
        { header: "Rate", token: "{rate}", width: 0.15, align: "right" },
        { header: "Hours", token: "{hours}", width: 0.13, align: "right" },
        { header: "Current", token: "{current}", width: 0.19, align: "right" },
        { header: "YTD", token: "{ytd}", width: 0.19, align: "right" },
      ],
    },

    // Deductions table (hidden for contractors, who have none withheld)
    { id: "ded-label", type: "text", x: 40, y: 330, w: 200, content: "TAXES & DEDUCTIONS", fontSize: 8, bold: true, color: "#14532d", showIf: "!isContractor" },
    {
      id: "ded-table", type: "table", x: 40, y: 344, w: 532, binding: "deductions", showIf: "!isContractor",
      rowHeight: 16, fontSize: 8, headerFill: "#f1f5f9", headerColor: "#334155", color: "#1a1a1a", zebra: false, rowLines: true,
      columns: [
        { header: "Description", token: "{name}", width: 0.55, align: "left" },
        { header: "Current", token: "{current}", width: 0.225, align: "right" },
        { header: "YTD", token: "{ytd}", width: 0.225, align: "right" },
      ],
    },

    // Summary
    { id: "sum-gross-l", type: "text", x: 312, y: 560, w: 130, content: "Gross Pay", fontSize: 9, color: "#475569" },
    { id: "sum-gross-v", type: "text", x: 442, y: 560, w: 130, content: "${grossPay}", fontSize: 9, color: "#1a1a1a", align: "right" },
    { id: "sum-ded-l", type: "text", x: 312, y: 576, w: 130, content: "Total Deductions", fontSize: 9, color: "#475569", showIf: "!isContractor" },
    { id: "sum-ded-v", type: "text", x: 442, y: 576, w: 130, content: "-${totalDeductions}", fontSize: 9, color: "#1a1a1a", align: "right", showIf: "!isContractor" },
    { id: "net-bg", type: "rect", x: 302, y: 594, w: 270, h: 30, fill: "#f0fdf4", stroke: "#bbf7d0", lineWidth: 0.75, radius: 4 },
    { id: "net-label", type: "text", x: 312, y: 602, w: 120, content: "NET PAY", fontSize: 10, bold: true, color: "#14532d" },
    { id: "net-value", type: "text", x: 432, y: 600, w: 130, content: "${netPay}", fontSize: 13, bold: true, color: "#14532d", align: "right" },

    // YTD footer strip
    { id: "ytd-line", type: "line", x: 40, y: 700, w: 532, h: 0, color: "#e2e8f0", lineWidth: 0.75 },
    { id: "ytd-gross", type: "text", x: 40, y: 710, w: 170, content: "YTD Gross: ${ytdGrossPay}", fontSize: 8, color: "#64748b" },
    { id: "ytd-tax", type: "text", x: 221, y: 710, w: 170, content: "YTD Taxes: ${ytdTotalTax}", fontSize: 8, color: "#64748b", align: "center" },
    { id: "ytd-net", type: "text", x: 402, y: 710, w: 170, content: "YTD Net Pay: ${ytdNetPay}", fontSize: 8, color: "#64748b", align: "right" },
  ],
};

// A second, lighter paystub look: no header band, ruled sections, mono-ish grid.
export const MINIMAL_PAYSTUB_LAYOUT = {
  page: { width: 612, height: 792 },
  elements: [
    { id: "m-company", type: "text", x: 40, y: 44, w: 320, content: "{company}", fontSize: 14, bold: true, color: "#111827" },
    { id: "m-addr", type: "text", x: 40, y: 62, w: 320, content: "{companyAddress} · {companyCityStateZip}", fontSize: 8, color: "#6b7280" },
    { id: "m-title", type: "text", x: 372, y: 44, w: 200, content: "PAY STATEMENT", fontSize: 11, bold: true, color: "#111827", align: "right" },
    { id: "m-period", type: "text", x: 372, y: 62, w: 200, content: "{payPeriod}", fontSize: 8, color: "#6b7280", align: "right" },
    { id: "m-rule1", type: "line", x: 40, y: 80, w: 532, h: 0, color: "#111827", lineWidth: 1.2 },

    { id: "m-emp", type: "text", x: 40, y: 92, w: 260, content: "{employeeName}", fontSize: 10, bold: true, color: "#111827" },
    { id: "m-empaddr", type: "text", x: 40, y: 106, w: 260, content: "{employeeAddress}, {employeeCityStateZip}", fontSize: 8, color: "#6b7280" },
    { id: "m-ssn", type: "text", x: 372, y: 92, w: 200, content: "SSN {ssn}", fontSize: 8, color: "#6b7280", align: "right" },
    { id: "m-paydate", type: "text", x: 372, y: 104, w: 200, content: "Paid {payDate} · {payFrequency}", fontSize: 8, color: "#6b7280", align: "right" },

    {
      id: "m-earn", type: "table", x: 40, y: 132, w: 532, binding: "earnings",
      rowHeight: 17, fontSize: 8, headerFill: "none", headerColor: "#111827", color: "#1f2937", zebra: false, rowLines: true,
      columns: [
        { header: "EARNINGS", token: "{name}", width: 0.4, align: "left" },
        { header: "RATE", token: "{rate}", width: 0.15, align: "right" },
        { header: "HOURS", token: "{hours}", width: 0.13, align: "right" },
        { header: "CURRENT", token: "{current}", width: 0.16, align: "right" },
        { header: "YTD", token: "{ytd}", width: 0.16, align: "right" },
      ],
    },
    {
      id: "m-ded", type: "table", x: 40, y: 268, w: 532, binding: "deductions", showIf: "!isContractor",
      rowHeight: 16, fontSize: 8, headerFill: "none", headerColor: "#111827", color: "#1f2937", zebra: false, rowLines: true,
      columns: [
        { header: "DEDUCTIONS", token: "{name}", width: 0.6, align: "left" },
        { header: "CURRENT", token: "{current}", width: 0.2, align: "right" },
        { header: "YTD", token: "{ytd}", width: 0.2, align: "right" },
      ],
    },

    { id: "m-rule2", type: "line", x: 40, y: 560, w: 532, h: 0, color: "#111827", lineWidth: 1.2 },
    { id: "m-gross-l", type: "text", x: 40, y: 572, w: 150, content: "Gross {grossPay}", fontSize: 9, color: "#6b7280" },
    { id: "m-ded-l", type: "text", x: 200, y: 572, w: 180, content: "Deductions {totalDeductions}", fontSize: 9, color: "#6b7280", align: "center", showIf: "!isContractor" },
    { id: "m-net", type: "text", x: 392, y: 566, w: 180, content: "NET ${netPay}", fontSize: 14, bold: true, color: "#111827", align: "right" },
    { id: "m-ytd", type: "text", x: 40, y: 700, w: 532, content: "Year to date: Gross ${ytdGrossPay} · Taxes ${ytdTotalTax} · Net ${ytdNetPay}", fontSize: 8, color: "#9ca3af", align: "center" },
  ],
};

export const DEFAULT_CANADIAN_PAYSTUB_LAYOUT = {
  page: { width: 612, height: 792 },
  elements: DEFAULT_PAYSTUB_LAYOUT.elements.map((el) => {
    if (el.id === "hdr-title") return { ...el, content: "STATEMENT OF EARNINGS" };
    if (el.id === "emp-ssn") return { ...el, content: "" };
    if (el.id === "emp-filing") return { ...el, content: "" };
    return { ...el };
  }),
};

export const DEFAULT_OFFER_LETTER_LAYOUT = {
  page: { width: 612, height: 792 },
  elements: [
    { id: "o-logo", type: "image", x: 40, y: 40, w: 90, h: 36, src: "{logoDataUrl}", showIf: "hasLogo" },
    { id: "o-company", type: "text", x: 40, y: 44, w: 340, content: "{companyName}", fontSize: 16, bold: true, color: "#14532d" },
    { id: "o-caddr", type: "text", x: 372, y: 44, w: 200, content: "{companyAddress}", fontSize: 8, color: "#64748b", align: "right" },
    { id: "o-ccsz", type: "text", x: 372, y: 56, w: 200, content: "{companyCityStateZip}", fontSize: 8, color: "#64748b", align: "right" },
    { id: "o-ccontact", type: "text", x: 372, y: 68, w: 200, content: "{companyPhone}  {companyEmail}", fontSize: 8, color: "#64748b", align: "right" },
    { id: "o-rule", type: "line", x: 40, y: 86, w: 532, h: 0, color: "#14532d", lineWidth: 1.5 },

    { id: "o-date", type: "text", x: 40, y: 104, w: 200, content: "{letterDate}", fontSize: 9, color: "#475569" },
    { id: "o-cand", type: "text", x: 40, y: 126, w: 300, content: "{candidateName}", fontSize: 10, bold: true, color: "#1a1a1a" },
    { id: "o-candaddr", type: "text", x: 40, y: 140, w: 300, content: "{candidateAddress}", fontSize: 9, color: "#475569" },
    { id: "o-candcsz", type: "text", x: 40, y: 152, w: 300, content: "{candidateCityStateZip}", fontSize: 9, color: "#475569" },

    { id: "o-re", type: "text", x: 40, y: 178, w: 532, content: "RE: Offer of Employment ({jobTitle})", fontSize: 11, bold: true, color: "#14532d" },
    { id: "o-dear", type: "text", x: 40, y: 202, w: 532, content: "Dear {candidateName},", fontSize: 10, color: "#1a1a1a" },
    {
      id: "o-open", type: "text", x: 40, y: 222, w: 532, wrap: true, fontSize: 10, color: "#334155",
      content: "We are pleased to extend this offer of employment to you for the position of {jobTitle} at {companyName}. We were impressed with your qualifications and believe you will be a valuable addition to our team.",
    },

    { id: "o-details-label", type: "text", x: 40, y: 288, w: 300, content: "POSITION DETAILS", fontSize: 8, bold: true, color: "#14532d" },
    {
      id: "o-details", type: "table", x: 40, y: 302, w: 532, binding: "offerDetails",
      rowHeight: 18, fontSize: 9, headerFill: "none", headerColor: "#334155", color: "#1f2937", zebra: true, rowLines: false,
      columns: [
        { header: "", token: "{label}", width: 0.35, align: "left" },
        { header: "", token: "{value}", width: 0.65, align: "left" },
      ],
    },

    {
      id: "o-comp", type: "text", x: 40, y: 448, w: 532, wrap: true, fontSize: 10, color: "#334155",
      content: "Your compensation will be {compensation}, paid {payFrequency}.",
    },
    { id: "o-benefits-label", type: "text", x: 40, y: 482, w: 300, content: "BENEFITS", fontSize: 8, bold: true, color: "#14532d", showIf: "hasBenefits" },
    { id: "o-benefits", type: "text", x: 40, y: 496, w: 532, wrap: true, fontSize: 9, color: "#334155", content: "{benefits}", showIf: "hasBenefits" },
    { id: "o-terms", type: "text", x: 40, y: 560, w: 532, wrap: true, fontSize: 9, color: "#334155", content: "{additionalTerms}" },
    {
      id: "o-deadline", type: "text", x: 40, y: 600, w: 532, wrap: true, fontSize: 10, color: "#334155", showIf: "hasDeadline",
      content: "Please confirm your acceptance of this offer by {responseDeadline}.",
    },

    { id: "o-closing", type: "text", x: 40, y: 648, w: 300, content: "Sincerely,", fontSize: 10, color: "#1a1a1a" },
    { id: "o-sigline", type: "line", x: 40, y: 700, w: 180, h: 0, color: "#94a3b8", lineWidth: 0.75 },
    { id: "o-signer", type: "text", x: 40, y: 708, w: 250, content: "{signerName}", fontSize: 10, bold: true, color: "#1a1a1a" },
    { id: "o-signertitle", type: "text", x: 40, y: 722, w: 250, content: "{signerTitle}", fontSize: 9, color: "#64748b" },
  ],
};

// Port of the built-in Gusto template (generateTemplateA): teal section bars,
// gray company/worker boxes, split employee/employer tax tables, itemized
// deduction/contribution tables and the summary block.
const GUSTO_TEAL = "#00a8a1";
const GUSTO_HDR = "#6a6a6a";

// Faithful port of the built-in Gusto template (generateTemplateA): same
// coordinates, fonts, colours and table styling, and the sections below the
// earnings table flow after it (`after`/`offset`) exactly as the generator's
// running y does, so any number of rows lays out the way the real one does.
// Text y = the generator's baseline − 0.85 × font size; table y = the
// generator's first baseline − 11 (its rows draw text 11pt below the row top).
const gustoTable = (over) => ({
  type: "table", rowHeight: 16, fontSize: 9, headerFontSize: 9,
  headerFill: "none", headerColor: "#000000", color: "#000000",
  zebra: true, zebraFill: "#f5f5f5", zebraStart: 0,
  rowLines: false, colLines: true, colLineColor: "#c8c8c8", colLineWidth: 0.3,
  padX: 4, padRight: 4, textOffset: 11, headerTextOffset: 11,
  ...over,
});
const gustoMoneyCols = (first, firstWidth, extra = []) => [
  { header: first, token: "{name}", width: firstWidth, align: "left" },
  ...extra,
  { header: "Current", token: "${current}", width: (1 - firstWidth - extra.reduce((a, c) => a + c.width, 0)) / 2, align: "right" },
  { header: extra.length ? "Year-To-Date" : "YTD", token: "${ytd}", width: (1 - firstWidth - extra.reduce((a, c) => a + c.width, 0)) / 2, align: "right" },
];
export const GUSTO_PAYSTUB_LAYOUT = {
  page: { width: 612, height: 792 },
  metadata: { title: "Gusto", creator: "wkhtmltopdf 0.12.6.1", producer: "Qt 4.8.7" },
  elements: [
    // Header: the customer's logo (fit inside 120×35) or the Gusto wordmark image
    { id: "g-logo", type: "image", x: 40, y: 40, w: 120, h: 35, fit: "contain", src: "{logoDataUrl}", showIf: "hasLogo" },
    { id: "g-logo-gusto", type: "image", x: 40, y: 40, w: 70, src: "{gustoLogo}", showIf: "!hasLogo" },
    { id: "g-title", type: "text", x: 40, y: 84.7, w: 300, content: "{statementTitle}", fontSize: 18, bold: true, color: "#000000" },
    { id: "g-period", type: "text", x: 40, y: 122.35, w: 420, content: "Pay period: {startDate} – {endDate}   Pay Day: {payDate}", fontSize: 9, color: "#000000" },
    { id: "g-namebank", type: "text", x: 40, y: 134.35, w: 320, content: "{employeeName} (...******{bankLast4})", fontSize: 9, color: "#000000" },

    // Company / worker boxes
    { id: "g-box1", type: "rect", x: 310, y: 70, w: 130, h: 70, fill: "#f8f8f8", stroke: "none" },
    { id: "g-box2", type: "rect", x: 450, y: 70, w: 130, h: 70, fill: "#f8f8f8", stroke: "none" },
    { id: "g-box1-h", type: "text", x: 318, y: 77.2, w: 114, content: "Company", fontSize: 8, bold: true, color: "#000000" },
    { id: "g-box2-h", type: "text", x: 458, y: 77.2, w: 114, content: "{workerLabelTitle}", fontSize: 8, bold: true, color: "#000000" },
    { id: "g-box1-l1", type: "text", x: 318, y: 92.05, w: 114, content: "{company}", fontSize: 7, color: "#000000" },
    { id: "g-box1-l2", type: "text", x: 318, y: 104.05, w: 114, content: "{companyAddress}", fontSize: 7, color: "#000000" },
    { id: "g-box1-l3", type: "text", x: 318, y: 116.05, w: 114, content: "{companyCityLine}", fontSize: 7, color: "#000000" },
    { id: "g-box1-l4", type: "text", x: 318, y: 128.05, w: 114, content: "{companyPhone}", fontSize: 7, color: "#000000" },
    { id: "g-box2-l1", type: "text", x: 458, y: 92.05, w: 114, content: "{employeeName}", fontSize: 7, color: "#000000" },
    { id: "g-box2-l2", type: "text", x: 458, y: 104.05, w: 114, content: "{ssnLine}", fontSize: 7, color: "#000000" },
    { id: "g-box2-l3", type: "text", x: 458, y: 116.05, w: 114, content: "{employeeAddress}", fontSize: 7, color: "#000000" },
    { id: "g-box2-l4", type: "text", x: 458, y: 128.05, w: 114, content: "{employeeCityLine}", fontSize: 7, color: "#000000" },

    // Earnings
    { id: "g-earn-h", type: "text", x: 40, y: 163.5, w: 300, content: "{earningsTitle}", fontSize: 10, bold: true, color: GUSTO_HDR },
    { id: "g-earn-bar", type: "rect", x: 39.5, y: 175, w: 533, h: 1, fill: GUSTO_TEAL, stroke: "none" },
    gustoTable({
      id: "g-earn-table", x: 40, y: 181, w: 532, binding: "earnings", underlineFirstCol: "Regular Hours",
      columns: [
        { header: "Description", token: "{nameDetailed}", width: 0.45, align: "left" },
        { header: "Rate", token: "{rateDetailed}", width: 0.13, align: "right" },
        { header: "Hours", token: "{hours}", width: 0.1, align: "right" },
        { header: "Current", token: "${current}", width: 0.14, align: "right" },
        { header: "Year-To-Date", token: "${ytd}", width: 0.18, align: "right" },
      ],
    }),

    // Employee / employer taxes (two columns) — employees only, 12pt under the earnings table
    { id: "g-emptax-h", type: "text", x: 40, w: 240, content: "Employee Taxes Withheld", fontSize: 10, bold: true, color: GUSTO_HDR, after: "g-earn-table", offset: 14.5, showIf: "!isContractor" },
    { id: "g-emptax-bar", type: "rect", x: 39.5, w: 262, h: 1, fill: GUSTO_TEAL, stroke: "none", after: "g-earn-table", offset: 26, showIf: "!isContractor" },
    { id: "g-ertax-h", type: "text", x: 311, w: 240, content: "Employer Tax", fontSize: 10, bold: true, color: GUSTO_HDR, after: "g-earn-table", offset: 14.5, showIf: "!isContractor" },
    { id: "g-ertax-bar", type: "rect", x: 310.5, w: 262, h: 1, fill: GUSTO_TEAL, stroke: "none", after: "g-earn-table", offset: 26, showIf: "!isContractor" },
    gustoTable({
      id: "g-emptax-table", x: 40, w: 261, binding: "employeeTaxes", after: "g-earn-table", offset: 30, showIf: "!isContractor",
      underlineFirstCol: true, bottomBorder: "#c8c8c8",
      columns: gustoMoneyCols("Description", 0.6),
    }),
    gustoTable({
      id: "g-ertax-table", x: 311, w: 261, binding: "employerTaxes", after: "g-earn-table", offset: 30, showIf: "!isContractor",
      underlineFirstCol: true, bottomBorder: "#c8c8c8",
      columns: gustoMoneyCols("Company Tax", 0.6),
    }),

    // Deductions / contributions — employees only, each 10pt under the table above
    { id: "g-ded-h", type: "text", x: 40, w: 300, content: "Employee Deductions", fontSize: 10, bold: true, color: GUSTO_HDR, after: "g-emptax-table", offset: 12.5, showIf: "!isContractor" },
    { id: "g-ded-bar", type: "rect", x: 39.5, w: 533, h: 1, fill: GUSTO_TEAL, stroke: "none", after: "g-emptax-table", offset: 24, showIf: "!isContractor" },
    gustoTable({
      id: "g-ded-table", x: 40, w: 532, binding: "deductionItems", after: "g-emptax-table", offset: 28, showIf: "!isContractor",
      bottomBorder: "#c8c8c8",
      columns: gustoMoneyCols("Description", 0.55, [{ header: "Tax Type", token: "{taxType}", width: 0.15, align: "right" }]),
    }),
    { id: "g-con-h", type: "text", x: 40, w: 300, content: "Employee Contributions", fontSize: 10, bold: true, color: GUSTO_HDR, after: "g-ded-table", offset: 12.5, showIf: "!isContractor" },
    { id: "g-con-bar", type: "rect", x: 39.5, w: 533, h: 1, fill: GUSTO_TEAL, stroke: "none", after: "g-ded-table", offset: 24, showIf: "!isContractor" },
    gustoTable({
      id: "g-con-table", x: 40, w: 532, binding: "contributionItems", after: "g-ded-table", offset: 28, showIf: "!isContractor",
      bottomBorder: "#c8c8c8",
      columns: gustoMoneyCols("Description", 0.55, [{ header: "Tax Type", token: "{taxType}", width: 0.15, align: "right" }]),
    }),

    // Summary (employees) — 10pt under the contributions table
    { id: "g-sum-h", type: "text", x: 40, w: 300, content: "Summary", fontSize: 10, bold: true, color: GUSTO_HDR, after: "g-con-table", offset: 12.5, showIf: "!isContractor" },
    { id: "g-sum-bar", type: "rect", x: 39.5, w: 533, h: 1, fill: GUSTO_TEAL, stroke: "none", after: "g-con-table", offset: 24, showIf: "!isContractor" },
    gustoTable({
      id: "g-sum-table", x: 40, w: 532, binding: "summary", after: "g-con-table", offset: 28, showIf: "!isContractor",
      underlineFirstCol: true, bottomBorder: "#c8c8c8",
      columns: [
        { header: "Description", token: "{name}", width: 0.55, align: "left" },
        { header: "Current", token: "{current}", width: 0.225, align: "right" },
        { header: "Year-To-Date", token: "{ytd}", width: 0.225, align: "right" },
      ],
    }),

    // Contractors: the tax notice replaces the tax/deduction sections, then the summary
    { id: "g-notax-h", type: "text", x: 40, w: 300, content: "Tax Information", fontSize: 10, bold: true, color: GUSTO_HDR, after: "g-earn-table", offset: 14.5, showIf: "isContractor" },
    { id: "g-notax-bar", type: "rect", x: 39.5, w: 533, h: 1, fill: GUSTO_TEAL, stroke: "none", after: "g-earn-table", offset: 26, showIf: "isContractor" },
    { id: "g-notax-1", type: "text", x: 40, w: 532, content: "No taxes withheld. As an independent contractor (1099), you are responsible for", fontSize: 9, color: "#666666", after: "g-earn-table", offset: 33.35, showIf: "isContractor" },
    { id: "g-notax-2", type: "text", x: 40, w: 532, content: "paying self-employment taxes and any applicable federal/state income taxes.", fontSize: 9, color: "#666666", after: "g-earn-table", offset: 45.35, showIf: "isContractor" },
    { id: "g-sumc-h", type: "text", x: 40, w: 300, content: "Summary", fontSize: 10, bold: true, color: GUSTO_HDR, after: "g-notax-2", offset: 18.3, showIf: "isContractor" },
    { id: "g-sumc-bar", type: "rect", x: 39.5, w: 533, h: 1, fill: GUSTO_TEAL, stroke: "none", after: "g-notax-2", offset: 29.8, showIf: "isContractor" },
    gustoTable({
      id: "g-sumc-table", x: 40, w: 532, binding: "summary", after: "g-notax-2", offset: 33.8, showIf: "isContractor",
      underlineFirstCol: true, bottomBorder: "#c8c8c8",
      columns: [
        { header: "Description", token: "{name}", width: 0.55, align: "left" },
        { header: "Current", token: "{current}", width: 0.225, align: "right" },
        { header: "Year-To-Date", token: "{ytd}", width: 0.225, align: "right" },
      ],
    }),
  ],
};

// Faithful port of the built-in OnPay check-stub template (generateTemplateH):
// 15pt margin, blue banners, Courier 6pt grids padded to 25 rows with the
// light-grey sub-headers and black side borders, a totals line and the
// four-column footer. Everything is fixed-size in the original, so every
// coordinate here is absolute.
const ONPAY_BLUE = "#2580d8";
const ONPAY_M = 15;
const ONPAY_TW = 612 - 2 * ONPAY_M;               // 582
const ONPAY_C1W = ONPAY_TW * 0.40, ONPAY_C2W = ONPAY_TW * 0.28, ONPAY_C3W = ONPAY_TW * 0.32;
const ONPAY_C1X = ONPAY_M, ONPAY_C2X = ONPAY_M + ONPAY_C1W, ONPAY_C3X = ONPAY_M + ONPAY_C1W + ONPAY_C2W;
const ONPAY_TABLE_Y = 178;                        // blue title bars
const ONPAY_GRID_Y = ONPAY_TABLE_Y + 12;           // grey sub-header row
const ONPAY_TOTALS_Y = ONPAY_GRID_Y + 10 + 25 * 8; // 400
const ONPAY_FOOT_Y = ONPAY_TOTALS_Y + 8 + 8;       // 416
const ONPAY_FCW = ONPAY_TW / 4;                    // 145.5
const onpayTable = (over) => ({
  type: "table", font: "courier", fontSize: 6, headerFontSize: 6, headerBold: false,
  headerFill: "#d7d7d7", headerStroke: "#c8c8c8", headerColor: "#000000", color: "#000000",
  headerHeight: 10, rowHeight: 8, minRows: 25, headerTextOffset: 7, textOffset: 6,
  padX: 2, padRight: 3, zebra: false, rowLines: false,
  colLines: true, colLineColor: "#c8c8c8", colLineWidth: 0.5, headerColLines: false,
  sideBorders: "#000000", sideBorderWidth: 0.5, bottomBorder: "#000000", bottomBorderWidth: 0.5,
  ...over,
});
const onpayTitle = (id, x, w, text) => [
  { id: `${id}-hdr`, type: "rect", x, y: ONPAY_TABLE_Y, w, h: 12, fill: ONPAY_BLUE, stroke: "none" },
  { id: `${id}-hdr-t`, type: "text", x, y: ONPAY_TABLE_Y + 1.2, w, content: text, fontSize: 8, bold: true, color: "#ffffff", align: "center" },
];
const onpayTotalsBox = (id, x, w) => [
  { id: `${id}-tot-bg`, type: "rect", x, y: ONPAY_TOTALS_Y, w, h: 10, fill: "#ffffff", stroke: "none" },
  { id: `${id}-tot-top`, type: "line", x, y: ONPAY_TOTALS_Y, w, h: 0, color: "#c8c8c8", lineWidth: 0.5 },
  { id: `${id}-tot-l`, type: "line", x, y: ONPAY_TOTALS_Y, w: 0, h: 10, color: "#000000", lineWidth: 0.5 },
  { id: `${id}-tot-r`, type: "line", x: x + w, y: ONPAY_TOTALS_Y, w: 0, h: 10, color: "#000000", lineWidth: 0.5 },
  { id: `${id}-tot-b`, type: "line", x, y: ONPAY_TOTALS_Y + 10, w, h: 0, color: "#000000", lineWidth: 0.5 },
];
const onpayFootHeader = (i, text) => [
  { id: `h-foot-hdr-${i}`, type: "rect", x: ONPAY_M + i * ONPAY_FCW, y: ONPAY_FOOT_Y, w: ONPAY_FCW, h: 12, fill: ONPAY_BLUE, stroke: "none" },
  { id: `h-foot-hdr-t-${i}`, type: "text", x: ONPAY_M + i * ONPAY_FCW, y: ONPAY_FOOT_Y + 1.2, w: ONPAY_FCW, content: text, fontSize: 8, bold: true, color: "#ffffff", align: "center" },
];
const onpayFootText = (id, col, line, content) => ({
  id, type: "text", x: ONPAY_M + col * ONPAY_FCW + 3, y: ONPAY_FOOT_Y + 15 + 5 + 8 * line - 5.1, w: ONPAY_FCW - 6, content, fontSize: 6, color: "#000000",
});
export const ONPAY_PAYSTUB_LAYOUT = {
  page: { width: 612, height: 792 },
  metadata: { creator: "wkhtmltopdf 0.12.6.1", producer: "Qt 4.8.7" },
  elements: [
    // Top: employee name, ***DD*** badge, the direct-deposit banner
    { id: "h-name-top", type: "text", x: 65, y: 6.8, w: 300, content: "{employeeName}", fontSize: 12, bold: true, color: "#000000" },
    { id: "h-dd-badge", type: "rect", x: 562, y: 10, w: 40, h: 12, fill: ONPAY_BLUE, stroke: "none" },
    { id: "h-dd-badge-t", type: "text", x: 567, y: 11.2, w: 40, content: "***DD***", fontSize: 8, bold: true, color: "#ffffff" },
    { id: "h-banner", type: "rect", x: 15, y: 27, w: 582, h: 20, fill: ONPAY_BLUE, stroke: "none" },
    { id: "h-banner-t", type: "text", x: 25, y: 30.8, w: 570, content: "DIRECT DEPOSIT *** DIRECT DEPOSIT **************************************************************************", fontSize: 12, color: "#ffffff" },

    // Employee address, thanks line, VOID mark
    { id: "h-addr1", type: "text", x: 20, y: 65.35, w: 280, content: "{employeeName}", fontSize: 9, color: "#000000" },
    { id: "h-addr2", type: "text", x: 20, y: 75.35, w: 280, content: "{employeeAddress}", fontSize: 9, color: "#000000" },
    { id: "h-addr3", type: "text", x: 20, y: 85.35, w: 280, content: "{employeeCityLine}", fontSize: 9, color: "#000000" },
    { id: "h-thanks", type: "text", x: 20, y: 106.2, w: 280, content: "Thank you for your hard work.", fontSize: 8, color: "#000000" },
    { id: "h-void", type: "text", x: 467, y: 117.7, w: 130, content: "*** VOID ***", fontSize: 18, bold: true, color: "#000000" },

    // Check-info bar
    { id: "h-nameband", type: "rect", x: 17, y: 134, w: 155, h: 12, fill: ONPAY_BLUE, stroke: "none" },
    { id: "h-nameband-t", type: "text", x: 21, y: 135.35, w: 150, content: "{employeeName}", fontSize: 9, bold: true, color: "#ffffff" },
    { id: "h-check-l", type: "text", x: 20, y: 150.05, w: 40, content: "Check #:", fontSize: 7, bold: true, color: "#000000" },
    { id: "h-check-v", type: "text", x: 50, y: 150.05, w: 60, content: "{checkNumber}", fontSize: 7, color: "#000000" },
    { id: "h-cdate-l", type: "text", x: 105, y: 150.05, w: 60, content: "Check Date:", fontSize: 7, bold: true, color: "#000000" },
    { id: "h-cdate-v", type: "text", x: 160, y: 150.05, w: 70, content: "{payDateMDY}", fontSize: 7, color: "#000000" },
    { id: "h-pstart-l", type: "text", x: 20, y: 162.05, w: 50, content: "Period Start:", fontSize: 7, bold: true, color: "#000000" },
    { id: "h-pstart-v", type: "text", x: 65, y: 162.05, w: 60, content: "{startDateMDY}", fontSize: 7, color: "#000000" },
    { id: "h-pend-l", type: "text", x: 105, y: 162.05, w: 60, content: "Period Ending:", fontSize: 7, bold: true, color: "#000000" },
    { id: "h-pend-v", type: "text", x: 160, y: 162.05, w: 70, content: "{endDateMDY}", fontSize: 7, color: "#000000" },
    { id: "h-memo-l", type: "text", x: 272, y: 141.05, w: 40, content: "MEMO:", fontSize: 7, bold: true, color: "#000000" },
    { id: "h-memo-v", type: "text", x: 302, y: 141.05, w: 280, content: "{memo}", fontSize: 7, color: "#000000" },
    { id: "h-emp-l", type: "text", x: 272, y: 152.05, w: 40, content: "EMP#:", fontSize: 7, bold: true, color: "#000000" },
    { id: "h-emp-v", type: "text", x: 302, y: 152.05, w: 200, content: "{employeeNumber}", fontSize: 7, color: "#000000" },

    // Three side-by-side grids: Gross Wages / Withholding Taxes / Deductions-Benefits
    ...onpayTitle("h-t1", ONPAY_C1X, ONPAY_C1W, "Gross Wages"),
    onpayTable({
      id: "h-t1", x: ONPAY_C1X, y: ONPAY_GRID_Y, w: ONPAY_C1W, binding: "onpayEarnings",
      columns: [
        { header: "Desc", token: "{desc}", width: 0.28, align: "left" },
        { header: "Hours", headerAlign: "center", token: "{hours}", width: 0.16, align: "right" },
        { header: "Rate", headerAlign: "center", token: "{rate}", width: 0.18, align: "right" },
        { header: "Amt", headerAlign: "center", token: "{amt}", width: 0.18, align: "right" },
        { header: "YTD", headerAlign: "center", token: "{ytd}", width: 0.20, align: "right", padRight: 5 },
      ],
    }),
    ...onpayTitle("h-t2", ONPAY_C2X, ONPAY_C2W, "Withholding Taxes"),
    onpayTable({
      id: "h-t2", x: ONPAY_C2X, y: ONPAY_GRID_Y, w: ONPAY_C2W, binding: "onpayTaxes",
      columns: [
        { header: "Desc", token: "{desc}", width: 0.50, align: "left" },
        { header: "Amt", headerAlign: "center", token: "{amt}", width: 0.25, align: "right" },
        { header: "YTD", headerAlign: "center", token: "{ytd}", width: 0.25, align: "right", padRight: 5 },
      ],
    }),
    ...onpayTitle("h-t3", ONPAY_C3X, ONPAY_C3W, "Deductions/Benefits"),
    onpayTable({
      id: "h-t3", x: ONPAY_C3X, y: ONPAY_GRID_Y, w: ONPAY_C3W, binding: "onpayDeductions",
      columns: [
        { header: "Desc", token: "{desc}", width: 0.35, align: "left" },
        { header: "Ben", headerAlign: "center", token: "{ben}", width: 0.15, align: "right" },
        { header: "Amt", headerAlign: "center", token: "{amt}", width: 0.25, align: "right" },
        { header: "YTD", headerAlign: "center", token: "{ytd}", width: 0.25, align: "right", padRight: 5 },
      ],
    }),

    // Totals line under each grid
    ...onpayTotalsBox("h-t1", ONPAY_C1X, ONPAY_C1W),
    { id: "h-t1-tot-amt", type: "text", x: ONPAY_C1X + ONPAY_C1W * 0.80 - 3 - 70, y: ONPAY_TOTALS_Y + 0.9, w: 70, content: "{grossPay}", font: "courier", fontSize: 6, color: "#000000", align: "right" },
    { id: "h-t1-tot-ytd", type: "text", x: ONPAY_C1X + ONPAY_C1W - 5 - 70, y: ONPAY_TOTALS_Y + 0.9, w: 70, content: "{ytdGrossPay}", font: "courier", fontSize: 6, color: "#000000", align: "right" },
    ...onpayTotalsBox("h-t2", ONPAY_C2X, ONPAY_C2W),
    { id: "h-t2-tot-amt", type: "text", x: ONPAY_C2X + ONPAY_C2W * 0.75 - 3 - 70, y: ONPAY_TOTALS_Y + 0.9, w: 70, content: "{totalTax}", font: "courier", fontSize: 6, color: "#000000", align: "right" },
    { id: "h-t2-tot-ytd", type: "text", x: ONPAY_C2X + ONPAY_C2W - 5 - 70, y: ONPAY_TOTALS_Y + 0.9, w: 70, content: "{onpayYtdTax}", font: "courier", fontSize: 6, color: "#000000", align: "right" },
    ...onpayTotalsBox("h-t3", ONPAY_C3X, ONPAY_C3W),
    { id: "h-t3-tot-amt", type: "text", x: ONPAY_C3X + ONPAY_C3W * 0.75 - 3 - 70, y: ONPAY_TOTALS_Y + 0.9, w: 70, content: "{onpayTotalDed}", font: "courier", fontSize: 6, color: "#000000", align: "right" },
    { id: "h-t3-tot-ytd", type: "text", x: ONPAY_C3X + ONPAY_C3W - 5 - 70, y: ONPAY_TOTALS_Y + 0.9, w: 70, content: "{onpayYtdDed}", font: "courier", fontSize: 6, color: "#000000", align: "right" },

    // Footer: Accruals / ACH / Net Pay / Company on the light-grey band
    { id: "h-foot-bg", type: "rect", x: ONPAY_M, y: ONPAY_FOOT_Y, w: ONPAY_TW, h: 50, fill: "#f0f0f0", stroke: "none" },
    ...onpayFootHeader(0, "Accruals"), ...onpayFootHeader(1, "ACH"), ...onpayFootHeader(2, "Net Pay"), ...onpayFootHeader(3, "Company"),
    onpayFootText("h-acc-1", 0, 0, "Sick: 0.00 used | 0.00 accr | 0.00 remn"),
    onpayFootText("h-acc-2", 0, 1, "PTO: 0.00 used | 0.00 accr | 0.00 remn"),
    onpayFootText("h-ach-1", 1, 0, "Direct Deposit****{bankLast4Raw}"),
    onpayFootText("h-ach-2", 1, 1, "{netPayDollar}"),
    onpayFootText("h-net-1", 2, 0, "Net: {netPayDollar}"),
    onpayFootText("h-net-2", 2, 1, "Direct Deposit: {netPayDollar}"),
    onpayFootText("h-co-1", 3, 0, "{company}"),
    onpayFootText("h-co-2", 3, 1, "{companyAddress}"),
    onpayFootText("h-co-3", 3, 2, "{companyCityState}"),
    onpayFootText("h-co-4", 3, 3, "{companyZip}"),
    { id: "h-foot-div-1", type: "line", x: ONPAY_M + ONPAY_FCW, y: ONPAY_FOOT_Y + 12, w: 0, h: 38, color: "#646464", lineWidth: 0.3 },
    { id: "h-foot-div-2", type: "line", x: ONPAY_M + 2 * ONPAY_FCW, y: ONPAY_FOOT_Y + 12, w: 0, h: 38, color: "#646464", lineWidth: 0.3 },
    { id: "h-foot-div-3", type: "line", x: ONPAY_M + 3 * ONPAY_FCW, y: ONPAY_FOOT_Y + 12, w: 0, h: 38, color: "#646464", lineWidth: 0.3 },
    { id: "h-foot-l", type: "line", x: ONPAY_M, y: ONPAY_FOOT_Y + 12, w: 0, h: 38, color: "#646464", lineWidth: 0.3 },
    { id: "h-foot-r", type: "line", x: ONPAY_M + ONPAY_TW, y: ONPAY_FOOT_Y + 12, w: 0, h: 38, color: "#646464", lineWidth: 0.3 },
    { id: "h-foot-b", type: "line", x: ONPAY_M, y: ONPAY_FOOT_Y + 50, w: ONPAY_TW, h: 0, color: "#646464", lineWidth: 0.3 },
  ],
};

// Starter library shown when creating a new template.
export const DEFAULT_LEGAL_DOCUMENT_LAYOUT = {
  page: { width: 612, height: 792 },
  elements: [
    { id: "l-title", type: "text", x: 40, y: 48, w: 532, content: "{documentTitle}", fontSize: 17, bold: true, color: "#1a1a1a", align: "center" },
    { id: "l-eff", type: "text", x: 40, y: 74, w: 532, content: "Effective as of {effectiveDate}", fontSize: 9, color: "#475569", align: "center" },
    { id: "l-rule", type: "rect", x: 226, y: 92, w: 160, h: 2, fill: "#14532d", stroke: "none" },
    {
      id: "l-intro", type: "text", x: 40, y: 112, w: 532, wrap: true, fontSize: 9.5, color: "#1f2937",
      content: 'This agreement ("Agreement") is entered into as of {effectiveDate}, by and between {partyAName}, of {partyAAddress}, {partyACityStateZip} ("Party A"), and {partyBName}, of {partyBAddress}, {partyBCityStateZip} ("Party B").',
    },
    { id: "l-rec-h", type: "text", x: 40, y: 168, w: 300, content: "RECITALS", fontSize: 10, bold: true, color: "#14532d", showIf: "hasRecitals" },
    { id: "l-rec-bar", type: "rect", x: 40, y: 181, w: 532, h: 1, fill: "#d1d5db", stroke: "none", showIf: "hasRecitals" },
    { id: "l-rec", type: "text", x: 40, y: 190, w: 532, wrap: true, fontSize: 9, color: "#334155", content: "{recitals}", showIf: "hasRecitals" },
    { id: "l-terms-h", type: "text", x: 40, y: 258, w: 300, content: "TERMS AND CONDITIONS", fontSize: 10, bold: true, color: "#14532d" },
    { id: "l-terms-bar", type: "rect", x: 40, y: 271, w: 532, h: 1, fill: "#d1d5db", stroke: "none" },
    { id: "l-terms", type: "text", x: 40, y: 280, w: 532, wrap: true, fontSize: 9, color: "#1f2937", content: "{terms}" },
    { id: "l-add-h", type: "text", x: 40, y: 470, w: 300, content: "ADDITIONAL TERMS", fontSize: 10, bold: true, color: "#14532d", showIf: "hasAdditionalTerms" },
    { id: "l-add-bar", type: "rect", x: 40, y: 483, w: 532, h: 1, fill: "#d1d5db", stroke: "none", showIf: "hasAdditionalTerms" },
    { id: "l-add", type: "text", x: 40, y: 492, w: 532, wrap: true, fontSize: 9, color: "#334155", content: "{additionalTerms}", showIf: "hasAdditionalTerms" },
    {
      id: "l-law", type: "text", x: 40, y: 546, w: 532, wrap: true, fontSize: 9, color: "#334155", showIf: "hasGoverningState",
      content: "This Agreement shall be governed by and construed in accordance with the laws of the State of {governingState}.",
    },
    {
      id: "l-witness", type: "text", x: 40, y: 584, w: 532, wrap: true, fontSize: 9, bold: true, color: "#1f2937",
      content: "IN WITNESS WHEREOF, the parties have executed this Agreement as of the date first written above.",
    },
    // Party A signature block
    { id: "l-siga-img", type: "image", x: 40, y: 622, w: 150, h: 34, src: "{partyASignature}", showIf: "hasPartyASignatureImage" },
    { id: "l-siga-typed", type: "text", x: 40, y: 634, w: 220, content: "{partyASignatureName}", fontSize: 16, italic: true, color: "#1a1a1a", showIf: "!hasPartyASignatureImage" },
    { id: "l-siga-line", type: "line", x: 40, y: 660, w: 220, h: 0, color: "#1a1a1a", lineWidth: 0.8 },
    { id: "l-siga-name", type: "text", x: 40, y: 666, w: 220, content: "{partyAName} (Party A)", fontSize: 8.5, bold: true, color: "#1a1a1a" },
    { id: "l-siga-date", type: "text", x: 40, y: 679, w: 220, content: "Date: {partyASignDate}", fontSize: 8, color: "#475569" },
    // Party B signature block
    { id: "l-sigb-img", type: "image", x: 332, y: 622, w: 150, h: 34, src: "{partyBSignature}", showIf: "hasPartyBSignatureImage" },
    { id: "l-sigb-typed", type: "text", x: 332, y: 634, w: 220, content: "{partyBSignatureName}", fontSize: 16, italic: true, color: "#1a1a1a", showIf: "!hasPartyBSignatureImage" },
    { id: "l-sigb-line", type: "line", x: 332, y: 660, w: 220, h: 0, color: "#1a1a1a", lineWidth: 0.8 },
    { id: "l-sigb-name", type: "text", x: 332, y: 666, w: 220, content: "{partyBName} (Party B)", fontSize: 8.5, bold: true, color: "#1a1a1a" },
    { id: "l-sigb-date", type: "text", x: 332, y: 679, w: 220, content: "Date: {partyBSignDate}", fontSize: 8, color: "#475569" },
  ],
};

export const DEFAULT_RESUME_LAYOUT = {
  page: { width: 612, height: 792 },
  elements: [
    { id: "r-name", type: "text", x: 50, y: 46, w: 512, content: "{fullName}", fontSize: 22, bold: true, color: "#14532d" },
    { id: "r-contact", type: "text", x: 50, y: 76, w: 512, content: "{contactLine}", fontSize: 8.5, color: "#64748b" },
    { id: "r-rule", type: "rect", x: 50, y: 92, w: 512, h: 2, fill: "#14532d", stroke: "none" },
    { id: "r-sum-h", type: "text", x: 50, y: 106, w: 300, content: "PROFESSIONAL SUMMARY", fontSize: 10, bold: true, color: "#14532d", showIf: "hasSummary" },
    { id: "r-sum", type: "text", x: 50, y: 122, w: 512, wrap: true, fontSize: 9, color: "#334155", content: "{summary}", showIf: "hasSummary" },
    { id: "r-exp-h", type: "text", x: 50, y: 176, w: 300, content: "PROFESSIONAL EXPERIENCE", fontSize: 10, bold: true, color: "#14532d" },
    { id: "r-exp-bar", type: "rect", x: 50, y: 189, w: 512, h: 1, fill: "#d1d5db", stroke: "none" },
    { id: "r-exp", type: "text", x: 50, y: 198, w: 512, wrap: true, fontSize: 9, color: "#1f2937", content: "{experienceText}" },
    { id: "r-edu-h", type: "text", x: 50, y: 520, w: 300, content: "EDUCATION", fontSize: 10, bold: true, color: "#14532d" },
    { id: "r-edu-bar", type: "rect", x: 50, y: 533, w: 512, h: 1, fill: "#d1d5db", stroke: "none" },
    { id: "r-edu", type: "text", x: 50, y: 542, w: 512, wrap: true, fontSize: 9, color: "#1f2937", content: "{educationText}" },
    { id: "r-skills-h", type: "text", x: 50, y: 600, w: 300, content: "SKILLS", fontSize: 10, bold: true, color: "#14532d", showIf: "hasSkills" },
    { id: "r-skills-bar", type: "rect", x: 50, y: 613, w: 512, h: 1, fill: "#d1d5db", stroke: "none", showIf: "hasSkills" },
    { id: "r-skills", type: "text", x: 50, y: 622, w: 512, wrap: true, fontSize: 9, color: "#334155", content: "{skillsLine}", showIf: "hasSkills" },
  ],
};

// Chime-inspired accounting mockup: green wordmark header, member block,
// balance summary, and the running-balance transaction table.
// Faithful port of the built-in Chime statement (generateBankTemplateA):
// 25pt margin, the Chime logo, light-grey labels, the orange section titles,
// the ruled summary block, the six-column transaction list with a title-cased
// store name over the raw descriptor, and the Error Resolution page.
const CHIME_M = 25;
const CHIME_GREY = "#b4b4b4";
const CHIME_LABEL = "#9c9c9c";
const CHIME_ORANGE = "#e97032";
const CHIME_TX_Y = 335 + 10 * 16.67;              // summary block ends here
const chimeText = (id, x, baseline, size, content, extra = {}) => ({ id, type: "text", x, y: baseline - size * 0.85, w: 300, content, fontSize: size, color: "#000000", ...extra });
export const DEFAULT_BANK_STATEMENT_LAYOUT = {
  page: { width: 612, height: 792 },
  elements: [
    { id: "c-logo", type: "image", x: CHIME_M, y: 27, w: 65, h: 20, fit: "contain", src: "{logoDataUrl}", showIf: "hasLogo" },
    { id: "c-logo-chime", type: "image", x: CHIME_M, y: 27, w: 65, h: 20, src: "{chimeLogo}", showIf: "!hasLogo" },
    chimeText("c-ms", 387, 45, 7, "Member Services", { w: 200, align: "right", color: CHIME_GREY }),
    chimeText("c-phone", 538, 57, 7, "(844) 244-6363", { w: 60 }),
    chimeText("c-name", CHIME_M, 105, 7, "{accountName}"),
    chimeText("c-addr1", CHIME_M, 117, 7, "{accountAddress1}"),
    chimeText("c-addr2", CHIME_M, 129, 7, "{accountAddress2}"),
    chimeText("c-title", CHIME_M, 175, 16, "Checking Account Statement", { w: 400, color: CHIME_GREY }),
    chimeText("c-acct-l", CHIME_M, 205, 8, "Account number", { color: CHIME_LABEL }),
    chimeText("c-acct", CHIME_M, 219, 8, "{accountNumber}", { color: CHIME_GREY }),
    chimeText("c-period-l", CHIME_M, 241, 8, "Statement period", { color: CHIME_LABEL }),
    chimeText("c-period", CHIME_M, 255, 8, "{monthText} {statementRangeLong}", { w: 400, color: CHIME_GREY }),
    chimeText("c-issued", CHIME_M, 275, 7, "Issued by Stride Bank, N.A., Member FDIC", { color: CHIME_GREY }),

    // Summary: ruled two-column block, ending balance in blue
    chimeText("c-sum-h", CHIME_M, 325, 8, "Summary", { color: CHIME_ORANGE }),
    { id: "c-sum-top", type: "line", x: CHIME_M, y: 335, w: 220, h: 0, color: "#dddddd", lineWidth: 0.7 },
    {
      id: "c-sum", type: "table", x: CHIME_M, y: 335, w: 220, binding: "chimeSummaryRows",
      showHeader: false, rowHeight: 16.67, fontSize: 7, textOffset: 10, padX: 0, padRight: 0,
      rowLines: true, rowLineColor: "#dddddd", rowLineWidth: 0.7, rowLinesLast: false, colLines: false,
      color: "#000000", colorField: "color",
      columns: [
        { token: "{label}", width: 0.72, align: "left" },
        { token: "{value}", width: 0.28, align: "right" },
      ],
    },

    // Transactions
    chimeText("c-tx-h", CHIME_M, CHIME_TX_Y + 40, 8, "Transactions", { color: CHIME_ORANGE }),
    { id: "c-tx-line1", type: "line", x: CHIME_M, y: CHIME_TX_Y + 50, w: 562, h: 0, color: "#dcdcdc", lineWidth: 0.7 },
    chimeText("c-th-date", CHIME_M, CHIME_TX_Y + 64, 7, "TRANSACTION DATE", { bold: true, w: 90 }),
    chimeText("c-th-desc", CHIME_M + 90, CHIME_TX_Y + 64, 7, "DESCRIPTION", { bold: true, w: 200 }),
    chimeText("c-th-type", CHIME_M + 320, CHIME_TX_Y + 64, 7, "TYPE", { bold: true, w: 60 }),
    chimeText("c-th-amt", CHIME_M + 340, CHIME_TX_Y + 64, 7, "AMOUNT", { bold: true, w: 60, align: "right" }),
    chimeText("c-th-net", CHIME_M + 405, CHIME_TX_Y + 64, 7, "NET AMOUNT", { bold: true, w: 60, align: "right" }),
    chimeText("c-th-settle", CHIME_M + 470, CHIME_TX_Y + 64, 7, "SETTLEMENT DATE", { bold: true, w: 100 }),
    { id: "c-tx-line2", type: "line", x: CHIME_M, y: CHIME_TX_Y + 72, w: 562, h: 0, color: "#dcdcdc", lineWidth: 0.7 },
    {
      id: "c-tx", type: "table", x: CHIME_M, y: CHIME_TX_Y + 76, w: 562, binding: "transactions",
      showHeader: false, rowHeight: 14, subRowHeight: 21, fontSize: 7, textOffset: 6, subOffset: 8,
      padX: 0, padRight: 0, rowLines: false, colLines: false, color: "#000000",
      columns: [
        { token: "{dateShort}", width: 0.1601, align: "left" },
        { token: "{descriptionStore}", sub: "{descriptionSub}", subFontSize: 6, width: 0.4093, align: "left" },
        { token: "{type}", width: 0.0712, align: "left" },
        { token: "{amountSigned}", width: 0.0712, align: "right" },
        { token: "{amountSigned}", width: 0.1157, align: "right" },
        { token: "{settlementDate}", width: 0.1725, align: "left", padX: 5 },
      ],
    },

    // Page 2: error resolution procedures
    { id: "c-err-h", page: 2, type: "text", x: CHIME_M, y: 64.8, w: 400, content: "Error Resolution Procedures", fontSize: 12, color: "#333333" },
    { id: "c-err-1", page: 2, type: "text", x: CHIME_M, y: 106.5, w: 562, wrap: true, fontSize: 10, color: "#333333",
      content: "In case of errors or questions about your electronic transactions, call 1-844-244-6363, write to Chime Member Services, P.O. Box 417, San Francisco, CA 94104-0417, as soon as you can, if you think your statement or receipt is wrong or if you need more information about a transfer listed on the statement or receipt. We must hear from you no later than 60 days after we sent the FIRST statement on which the problem or error appeared." },
    { id: "c-err-2", page: 2, type: "text", x: CHIME_M, y: 178, w: 562, wrap: true, fontSize: 10, color: "#333333",
      content: "(1) Tell us your name and account number (if any).\n\n(2) Describe the error or the transfer you are unsure about, and explain as clearly as you can why you believe it is an error or why you need more information.\n\n(3) Tell us the dollar amount of the suspected error.\n\nWe will investigate your complaint and will correct any error promptly. If we take more than 10 business days to do this, we will credit your account for the amount you think is in error, so that you will have the use of the money during the time it takes us to complete our investigation." },
  ],
};

// The UK Payslip starter: OnPay's layout, positions and sizes as they are,
// with the words a UK payslip uses (BACS, not direct deposit; Payments and
// Tax & National Insurance; Leave; Employer; pounds), stamped as a UK design
const UK_WORDING = [
  ["***DD***", "BACS"],
  ["DIRECT DEPOSIT *** DIRECT DEPOSIT ", "PAID BY BACS *** PAID BY BACS *****"],
  ["Check #:", "Payslip #:"], ["Cheque #:", "Payslip #:"], ["Check Date:", "Pay Date:"], ["Cheque Date:", "Pay Date:"],
  ["Gross Wages", "Payments"], ["Withholding Taxes", "Tax & National Insurance"], ["Deductions/Benefits", "Other Deductions / Benefits"],
  ["Direct Deposit", "Paid by BACS"], ["PTO", "Holiday"], ["$", "£"],
];
const UK_EXACT = { Accruals: "Leave", ACH: "BACS", Company: "Employer", Regular: "Basic Pay" };
const ukWording = (value) => {
  if (typeof value === "string") {
    if (UK_EXACT[value]) return UK_EXACT[value];
    return UK_WORDING.reduce((s, [from, to]) => s.split(from).join(to), value);
  }
  if (Array.isArray(value)) return value.map(ukWording);
  if (value && typeof value === "object") {
    const out = {};
    for (const [k, v] of Object.entries(value)) out[k] = (k === "id" || k === "binding" || k === "type" || k === "font" || k === "color" || k === "fill" || k === "stroke") ? v : ukWording(v);
    return out;
  }
  return value;
};
export const UK_ONPAY_PAYSLIP_LAYOUT = { ...ukWording(ONPAY_PAYSTUB_LAYOUT), meta: { ...(ONPAY_PAYSTUB_LAYOUT.meta || {}), baseDocType: "uk-payslip" } };

export const STARTER_LAYOUTS = [
  { key: "blank-canvas", name: "Blank Canvas", description: "Empty page: design anything, assign a category when ready", documentType: "blank", layout: { page: { width: 612, height: 792 }, elements: [] } },
  { key: "paystub-gusto", name: "Gusto-Style Paystub", description: "Gusto Style Inspired Template", documentType: "paystub", layout: GUSTO_PAYSTUB_LAYOUT },
  { key: "paystub-onpay", name: "OnPay-Style Paystub", description: "OnPay Style Inspired Template", documentType: "paystub", layout: ONPAY_PAYSTUB_LAYOUT },
  { key: "paystub-classic", name: "Classic Green Paystub", description: "Clean MintSlip paystub with header band", documentType: "paystub", layout: DEFAULT_PAYSTUB_LAYOUT },
  { key: "paystub-minimal", name: "Minimal Ruled Paystub", description: "Minimal ruled pay statement", documentType: "paystub", layout: MINIMAL_PAYSTUB_LAYOUT },
  { key: "canadian-classic", name: "Canadian Paystub", description: "Canadian statement of earnings", documentType: "canadian-paystub", layout: DEFAULT_CANADIAN_PAYSTUB_LAYOUT },
  { key: "uk-onpay", name: "UK Payslip (OnPay Style)", description: "OnPay Style Inspired UK Payslip", documentType: "uk-payslip", layout: UK_ONPAY_PAYSLIP_LAYOUT },
  { key: "offer-letter", name: "Offer Letter", description: "Professional offer of employment", documentType: "offer-letter", layout: DEFAULT_OFFER_LETTER_LAYOUT },
  { key: "legal-document", name: "Legal Document", description: "Two-party agreement with signature blocks", documentType: "legal-document", layout: DEFAULT_LEGAL_DOCUMENT_LAYOUT },
  { key: "resume", name: "Resume", description: "Custom AI resume design", documentType: "resume", layout: DEFAULT_RESUME_LAYOUT },
  { key: "bank-statement-chime", name: "Accounting Mockup (Chime)", description: "Chime-style checking statement", documentType: "bank-statement", layout: DEFAULT_BANK_STATEMENT_LAYOUT },
  // the form generators' designs (Cease & Desist, Bill of Sale, ...)
  ...FORM_DOC_KEYS.map((key) => ({ key, name: FORM_DOC_TYPES[key].name, description: FORM_DOC_TYPES[key].description, documentType: key, layout: FORM_DOC_TYPES[key].layout })),
];
